import { useState, useRef, useCallback, useId, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, Monitor, Tablet, Smartphone, EyeOff, Undo2, Sparkles, Link, Link2, Unlink, ExternalLink, Loader2 } from 'lucide-react'
import { useBuilder } from './hooks/useBuilder'
import { BLOCK_TARGET_EVENT, buildBlockContext } from './chat/blockContext'
import { useChatTurns, blocTurnStatus } from './chat/turnStatus'
import { InlineRichTextEditor } from './editors/InlineRichTextEditor'
import { InlineSuggestions } from './editors/InlineSuggestions'
import { SUGGEST_LIST_ID, suggestOptionId } from './editors/inlineSuggest'
import {
  createSiteTerm,
  exactTerm,
  loadSiteTerms,
  matchTerms,
  toTermValue,
  vocabularyOf,
} from './editors/termRefStore'
import { FieldEditorPopover } from './editors/FieldEditorPopover'
import { resolveFieldDescriptor } from './editable/resolveFieldDescriptor'

import { pickEditorOptions, EDITOR_TITLES } from './editable/editorOptions'
import { BlockSettingsMenu } from './BlockSettingsMenu'
import { ImageSizingRail } from './ImageSizingRail'
import { snap, sourceRatio } from './editable/imageSizing'
import { getUnit } from './editable/unitRegistry'
import { getAtPath, setAtPath, applyRowAction } from './editable/pathUtils'
import { collectFormTokens } from './editable/formTokens'
import { unwrapDecoupled } from './editable/decoupledEnvelope'
import { unitEndpoint } from './editable/unitEndpoint'
import { SHARED_CARRIER_TYPE } from './BuilderExistingBlockPicker'
import { notify } from '../../notices'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { useOptionalAuth } from '../../context/AuthContext'
import { largeurUtile } from '../shell/cornerDock';

const DEVICE_ICONS = [
  { id: 'mobile', Icon: Smartphone, label: 'Mobile' },
  { id: 'tablet', Icon: Tablet, label: 'Tablet' },
  { id: 'desktop', Icon: Monitor, label: 'Desktop' },
]

// Multi-line inline editing reads the text as displayed (line breaks kept);
// jsdom has no innerText, hence the fallback.
const readText = (el, multiline) => (multiline ? (el.innerText ?? el.textContent) : el.textContent)

const normalizeText = (raw, multiline) => (multiline
  ? raw.replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
  : raw.replace(/\u00a0/g, ' ').trim())

/**
 * Les propositions d'une saisie de CAT\u00c9GORIE en place : les termes du site qui
 * contiennent la saisie, puis la cr\u00e9ation \u2014 offerte m\u00eame quand des noms
 * approchants remontent (\u00ab Drupal \u00bb \u00e0 c\u00f4t\u00e9 de \u00ab Drupal 11 \u00bb), mais jamais sur
 * un nom d\u00e9j\u00e0 pris, que le serveur refuserait.
 */
const termOptions = (terms, query) => {
  const typed = query.trim()
  const options = matchTerms(terms, typed).map((term) => ({
    key: `term-${term.id}`,
    kind: 'term',
    label: term.name,
    term,
  }))
  if (typed && !exactTerm(terms, typed)) {
    options.push({ key: 'create', kind: 'create', label: `Cr\u00e9er \u00ab ${typed} \u00bb`, name: typed })
  }
  return options
}

// How long a removed row can still be restored from the canvas.
const UNDO_WINDOW_MS = 10000

// Écrire dans un <input>/<textarea> PILOTÉ par React (reactive-json tient sa
// valeur) : poser `el.value` ne suffit pas — le traqueur interne de React
// croirait la valeur inchangée et n'émettrait aucun `onChange`. On passe donc
// par le setter natif du prototype, puis on émet l'événement `input` que React
// écoute : le store reactive-json se met à jour comme sous une frappe réelle.
const setNativeValue = (el, value) => {
  const proto = el instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  if (setter) setter.call(el, value)
  else el.value = value
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

// L'intitulé ÉDITABLE d'un contrôle de formulaire : son <label for> (les champs
// reactive-json posent toujours un id sur le contrôle), à défaut le <label> qui
// l'enveloppe. Rien d'autre — remonter plus haut viserait le titre du bloc.
// Null quand le contrôle n'a pas d'intitulé éditable : il garde alors son
// comportement vivant (facette, champ de recherche d'un aperçu).
const editableLabelOf = (control, scope) => {
  const id = control.getAttribute('id')
  let label = null
  if (id && scope) {
    try { label = scope.querySelector(`label[for="${CSS.escape(id)}"]`) } catch { label = null }
  }
  if (!label) label = control.closest('label')
  if (!label) return null
  return label.matches('[data-builder-field]') ? label : label.querySelector('[data-builder-field]')
}

// Panneau « où ce bloc est-il partagé ? » : la taille d'une tranche de la liste
// paginée, la largeur du panneau ancré, et la hauteur en dessous de laquelle il
// bascule au-dessus de la pastille plutôt qu'en dessous.
const USAGE_PAGE_SIZE = 10
const USAGE_PANEL_WIDTH = 380
const USAGE_PANEL_MIN_HEIGHT = 220

// ---- Row drag hit-test (pure DOM geometry, shared by dragover and drop) ----
// Two lists accept each other's rows when they are the SAME list of the same
// field, or two instances of one nested list — `0.columns.0.items` and
// `0.columns.1.items` are both `#.columns.#.items`, so a menu link moves from
// one column to the next. Lists of DIFFERENT fields (the four link columns of
// a footer) stay separate: they are four values, not one.
const listShape = (path) => String(path ?? '').replace(/\d+/g, '#')

// Hit-test only — how far the pointer may stray from a row and still aim at
// it (the gutter of a card grid, the block's own margin). NOT a visual value:
// nothing lights up at that distance, the aimed ROW does.
const ROW_AIM_TOLERANCE_PX = 96

const rowAccepts = (row, src) => (
  row.dataset.builderRowField === src.fieldKey
  && listShape(row.dataset.builderRowList) === listShape(src.arrayPath)
  // Same block as the grip that started the drag — see `blockEl` in `rowSourceOf`.
  && (!src.blockEl || row.closest('.builder-block-wrapper') === src.blockEl)
)

// Under the pointer: walk UP the chain of row roots and take the first
// COMPATIBLE one, not the innermost — a nested list would otherwise mask the
// list being reordered. `inside` says whether the pointer is in a row at all.
const rowUnderPointer = (target, src) => {
  let row = target instanceof Element ? target.closest('[data-builder-row-root]') : null
  const inside = row
  while (row) {
    if (rowAccepts(row, src)) return { row, inside }
    row = row.parentElement?.closest('[data-builder-row-root]') ?? null
  }
  return { row: null, inside }
}

// Outside every row — gutter of a card grid, margin of the block — the
// CLOSEST compatible row stays the target. Without this, crossing a gutter
// puts the indicator out and the drop does not happen.
const nearestRow = (src, fallbackScope, x, y) => {
  const scope = src.blockEl ?? fallbackScope
  if (!scope) return null
  let best = null
  let bestDist = ROW_AIM_TOLERANCE_PX
  scope.querySelectorAll('[data-builder-row-root]').forEach((row) => {
    if (!rowAccepts(row, src)) return
    const rect = row.getBoundingClientRect()
    // A twin hidden in CSS (mobile vs desktop menu) sits at 0,0 — closer
    // than anything actually on screen.
    if (!rect.width && !rect.height) return
    const dx = Math.max(rect.left - x, 0, x - rect.right)
    const dy = Math.max(rect.top - y, 0, y - rect.bottom)
    const dist = Math.hypot(dx, dy)
    if (dist < bestDist) { bestDist = dist; best = row }
  })
  return best
}

// The OTHER rows of the very same array (same field AND same list path), in
// the same block.
const siblingRows = (row) => {
  const scope = row.closest('.builder-block-wrapper')
  if (!scope) return []
  return [...scope.querySelectorAll('[data-builder-row-root]')].filter(other => (
    other.dataset.builderRowField === row.dataset.builderRowField
    && other.dataset.builderRowList === row.dataset.builderRowList
    && other.closest('.builder-block-wrapper') === scope
  ))
}

// A list of ONE row has no sibling to measure against: ask the CONTAINER how
// it lays its children out. Still not `data-builder-row-placement` — see below.
const loneRowAxis = (row) => {
  const parent = row.parentElement
  if (!parent || typeof getComputedStyle !== 'function') return 'y'
  const cs = getComputedStyle(parent)
  if (cs.display === 'grid' || cs.display === 'inline-grid') {
    // ONE track is a stacked list, whatever the rail was told.
    return cs.gridTemplateColumns.split(' ').filter(Boolean).length > 1 ? 'x' : 'y'
  }
  if (cs.display === 'flex' || cs.display === 'inline-flex') {
    return cs.flexDirection.startsWith('row') ? 'x' : 'y'
  }
  return getComputedStyle(row).display.startsWith('inline') ? 'x' : 'y'
}

// Stacked or side by side? It is MEASURED against the row the insertion would
// sit NEXT TO — the neighbour in the ARRAY (`data-builder-row-pos` ± 1), never
// the nearest one on screen: in a card grid that wraps, a 368×240 card has its
// vertical neighbour CLOSER than its horizontal one, which flipped the axis
// from one card to the next inside a single gesture.
// `data-builder-row-placement` cannot answer this at all: that attribute places
// the RAIL, and `detectPlacement` files every grid under `above` — including a
// ONE-COLUMN grid, which is stacked (the `bullets` and `steps` lists of the Lp
// blocks are exactly that). Reading the axis off it put the insertion bar on
// the LEFT of a vertical list.
// The result cannot change while the pointer is held, so it is cached for the
// duration of the gesture — `dragover` fires far too often to re-measure every
// sibling each time (a 4-column footer of 20 links = ~80 forced reflows/event).
const rowAxis = (row, cache) => {
  const hit = cache?.get(row)
  if (hit) return hit
  const rect = row.getBoundingClientRect()
  const pos = Number(row.dataset.builderRowPos)
  const ordered = siblingRows(row)
    .filter(other => other !== row)
    .map(other => ({ el: other, gap: Math.abs(Number(other.dataset.builderRowPos) - pos) }))
    .sort((a, b) => a.gap - b.gap)
  let axis = null
  for (const { el } of ordered) {
    const r = el.getBoundingClientRect()
    // A twin hidden in CSS (mobile vs desktop menu) sits at 0,0: it would
    // answer "x" for every list on the page.
    if (!r.width && !r.height) continue
    const dx = r.left + r.width / 2 - (rect.left + rect.width / 2)
    const dy = r.top + r.height / 2 - (rect.top + rect.height / 2)
    axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
    break
  }
  axis = axis ?? loneRowAxis(row)
  cache?.set(row, axis)
  return axis
}

// WHICH SIDE the row is entered from, on the axis the rows are laid out along.
// The axis is handed to the stylesheet (`data-builder-row-axis`) instead of
// being derived a second time there: the bar can then only be drawn on the
// edge the drop actually uses.
const markFor = (row, x, y, cache) => {
  const rect = row.getBoundingClientRect()
  const axis = rowAxis(row, cache)
  const after = axis === 'x'
    ? x > rect.left + rect.width / 2
    : y > rect.top + rect.height / 2
  return { mark: after ? 'after' : 'before', axis }
}

const parseOptions = (el) => {
  try {
    return JSON.parse(el.getAttribute('data-builder-options') ?? '{}') ?? {}
  } catch {
    return {}
  }
}

export const BuilderBlockWrapper = ({ bloc, index, total, children, onCommitField, nested = false }) => {
  const scrim = useScrimDismiss(() => setShowDeleteConfirm(false))
  const { isEditMode, moveBlock, removeBlock, replaceBlock, detachedShares, updateBlock, previewDevice, toggleBlockVisibility, registry, page, host, editScope, objectEdit } = useBuilder()
  // On regarde une INSTANCE D'OBJET à travers une page-modèle : le modèle est
  // une lunette, pas un contenu. Aucune commande de structure, aucune écriture
  // dans la page — seules ses liaisons `~~._context_.X` s'éditent, vers l'objet.
  const objectScope = editScope === 'object'
  // `useOptionalAuth` et non `useAuth` : ce composant rend AUSSI en PUBLIC
  // (BuilderPublicRenderer / SSG), hors de tout AuthProvider — `useAuth` y lève
  // et vide l'écran du site. Anonyme = aucune permission.
  const auth = useOptionalAuth()
  const hiddenOn = bloc.hiddenOn || []
  // The type's registry entry — carries the `defNid` the assistant is pinned to,
  // and the human label shown in the chat's context chip.
  const blockDef = useMemo(
    () => registry?.find(d => d.type === bloc.type) ?? null,
    [registry, bloc.type],
  )

  // --- Bloc partagé ---------------------------------------------------------
  // Ce bloc n'est qu'un PORTEUR : son contenu vit dans l'unité `sharedNid`, que
  // d'autres pages montent aussi. L'éditer ici les modifie toutes — il faut donc
  // le DIRE, sinon on édite « son » bloc sans savoir qu'on en touche cinq.
  const sharedNid = bloc.type === SHARED_CARRIER_TYPE
    ? (bloc.data?.sharedRef?.nid ?? null)
    : null
  const [sharedUsage, setSharedUsage] = useState(null)
  const [usageOffset, setUsageOffset] = useState(0)
  const [usageLoading, setUsageLoading] = useState(false)
  const [showUsage, setShowUsage] = useState(false)
  const [usagePos, setUsagePos] = useState(null)
  const usageBadgeRef = useRef(null)
  const usagePanelRef = useRef(null)
  const usageId = useId()

  // Une TRANCHE à la fois. Le premier appel (offset 0) sert deux choses : le
  // compte de la pastille (`pagesTotal`, indépendant de la tranche) et la
  // première page de la liste — un en-tête posé sur tout un site se compte en
  // centaines de pages, les charger toutes pour en montrer dix serait payer
  // le pire cas à chaque montage de bloc.
  useEffect(() => {
    setShowUsage(false)
    setUsageOffset(0)
    if (!isEditMode || !sharedNid) setSharedUsage(null)
  }, [isEditMode, sharedNid])

  useEffect(() => {
    if (!isEditMode || !sharedNid) return
    const ctrl = new AbortController()
    setUsageLoading(true)
    fetch(`/api/builder/unit-usage?nid=${sharedNid}&limit=${USAGE_PAGE_SIZE}&offset=${usageOffset}`, {
      credentials: 'include',
      signal: ctrl.signal,
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data) setSharedUsage(data) })
      .catch(err => {
        if (err.name !== 'AbortError') console.error('Builder: unit-usage failed', err)
      })
      .finally(() => { if (!ctrl.signal.aborted) setUsageLoading(false) })
    return () => ctrl.abort()
  }, [isEditMode, sharedNid, usageOffset])

  // Les pages annoncées par la pastille, et rien d'autre : le serveur les
  // filtre déjà par les droits, on n'ajoute ici aucune ligne de notre cru.
  const sharedPages = useMemo(() => sharedUsage?.pages ?? [], [sharedUsage])
  const usageTotal = sharedUsage?.pagesTotal ?? 0
  // Ce que le serveur a réellement servi, pas ce qu'on a demandé : c'est ce
  // qui garde « x–y sur N » d'accord avec les lignes affichées.
  const usageFrom = sharedUsage?.pageOffset ?? 0
  const hasPrevUsage = usageFrom > 0
  const hasNextUsage = usageFrom + sharedPages.length < usageTotal
  const hasHiddenUsage = (sharedUsage?.totalUsage ?? 0) > usageTotal

  const closeUsage = useCallback(({ refocus = true } = {}) => {
    setShowUsage(false)
    if (refocus) usageBadgeRef.current?.focus()
  }, [])

  // Panneau rendu en PORTAIL : la cellule d'une grille est positionnée par une
  // transformation CSS (elle devient le bloc conteneur d'un `position: fixed`)
  // et découpe en `overflow: hidden` — depuis la racine du document, ni l'un ni
  // l'autre ne s'applique. La position est donc recalculée à la main, au
  // défilement (en capture : celui du canevas ne remonte pas jusqu'à `window`)
  // et au redimensionnement.
  useEffect(() => {
    if (!showUsage) return
    const place = () => {
      const rect = usageBadgeRef.current?.getBoundingClientRect()
      if (!rect) return
      const left = Math.min(
        Math.max(8, rect.right - USAGE_PANEL_WIDTH),
        Math.max(8, largeurUtile() - USAGE_PANEL_WIDTH - 8),
      )
      setUsagePos(window.innerHeight - rect.bottom < USAGE_PANEL_MIN_HEIGHT
        ? { left, bottom: window.innerHeight - rect.top + 6 }
        : { left, top: rect.bottom + 6 })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [showUsage])

  useEffect(() => {
    if (!showUsage) return
    // Le panneau vit hors du sous-arbre de la pastille (portail) : les deux
    // comptent comme « dedans », sinon il se fermerait sur ses propres clics.
    const onDocPointerDown = (e) => {
      if (usageBadgeRef.current?.contains(e.target)) return
      if (usagePanelRef.current?.contains(e.target)) return
      setShowUsage(false)
    }
    document.addEventListener('mousedown', onDocPointerDown)
    return () => document.removeEventListener('mousedown', onDocPointerDown)
  }, [showUsage])

  // Échap referme et rend le focus à la pastille — sinon il repart à la racine
  // du document et l'on retraverse la page entière au clavier.
  useEscapeClose(closeUsage, showUsage)

  // Un seul endroit qui met des mots sur le partage : la pastille et son
  // équivalent lu à voix haute ne doivent pas raconter deux choses.
  const sharedLabel = useMemo(() => {
    if (!sharedUsage) {
      return {
        short: 'Partagé',
        long: "Bloc partagé : son contenu est commun à plusieurs emplacements, toute modification s'y répercute.",
      }
    }
    const visible = sharedUsage.pagesTotal ?? 0
    const hidden = (sharedUsage.totalUsage ?? 0) > visible
    const plural = visible > 1 ? 's' : ''
    return {
      short: `Partagé · ${hidden ? 'au moins ' : ''}${visible} page${plural}`,
      long: `Bloc partagé : son contenu est commun à ${hidden ? 'au moins ' : ''}${visible} page${plural}`
        + (hidden ? ', dont des contenus que vous ne voyez pas' : '')
        + ". Toute modification s'y répercute.",
    }
  }, [sharedUsage])

  // « Délier » : le porteur cède la place à un bloc ORDINAIRE portant une copie
  // du contenu. On lit la copie sur l'endpoint decoupled de l'unité — la même
  // source que celle que monte `UnitMount`. Rien n'est écrit ici : le bloc neuf
  // n'a pas de `nid`, donc la sauvegarde CRÉE une unité et ne touche jamais
  // celle qui reste partagée ailleurs.
  //
  // Le `sharedRef` du porteur part avec le remplacement (`detachedFrom`) : le
  // contexte le retient pour la session d'édition, ce qui transforme le bouton
  // en bascule — délier, puis relier au même contenu.
  const handleDetach = useCallback(async () => {
    if (!sharedNid) return
    const previousRef = bloc.data?.sharedRef ?? null
    try {
      const res = await fetch(unitEndpoint(sharedNid), { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = await res.json()
      // Ici l'appelant a le droit d'éditer, donc la forme imbriquée ; on ne parie
      // pas dessus pour autant — un `{}` silencieux créerait un bloc vide.
      const unit = unwrapDecoupled(body)
      const inner = unit.objectData ?? {}
      if (!unit.type) throw new Error('unité sans type de définition')
      replaceBlock(bloc.id, { type: unit.type }, {
        data: inner.data ?? {},
        hiddenOn: inner.hiddenOn ?? [],
        detachedFrom: previousRef?.nid ? previousRef : null,
      })
      notify({
        type: 'success',
        message: "Bloc délié : son contenu ne suit plus les autres pages. Tant que la page n'est pas rechargée, la barre du bloc permet de le relier.",
      })
    } catch (err) {
      console.error('Builder: detach shared block failed', err)
      notify({ type: 'error', message: 'Impossible de délier ce bloc.' })
    }
  }, [sharedNid, bloc.id, bloc.data, replaceBlock])

  // « Relier » : l'inverse exact, et le seul chemin de retour — le bloc
  // redevient un porteur vers l'unité commune. La copie faite au moment du
  // déliage est abandonnée (elle n'a pas de `nid` tant que la page n'a pas été
  // sauvegardée), d'où le rappel dans l'infobulle du bouton.
  const detachedFrom = detachedShares?.[bloc.id] ?? null
  const handleRelink = useCallback(() => {
    if (!detachedFrom?.nid) return
    replaceBlock(bloc.id, { type: SHARED_CARRIER_TYPE }, {
      data: { sharedRef: detachedFrom },
      hiddenOn: bloc.hiddenOn ?? [],
    })
    notify({
      type: 'success',
      message: 'Bloc relié : son contenu est de nouveau commun aux autres pages.',
    })
  }, [detachedFrom, bloc.id, bloc.hiddenOn, replaceBlock])
  // Structural defaults of the block type (`rjBuild.data`). A legacy (non-unit)
  // block renders `{...rjBuild.data, ...bloc.data}` (see BuilderBlockMount), so
  // a list that only ever lived in the seed is absent from `bloc.data`: an edit
  // must start from the rendered value, not from `undefined` — folding one cell
  // into an undefined array would silently drop every other row.
  const seedData = useMemo(
    () => blockDef?.rjBuild?.data ?? {},
    [blockDef],
  )
  const isHiddenOnCurrent = hiddenOn.includes(previewDevice)
  // L'assistant est-il en train de réécrire CE bloc ? Le tour dure — plusieurs
  // minutes pour une refonte — et il se termine par un REMPLACEMENT : la déf est
  // rechargée et l'instance remontée sur la nouvelle version. Entre les deux,
  // ce qui est à l'écran est périmé et ne doit plus être ni modifié ni déplacé :
  // une saisie en place serait écrasée sans avertissement par la réponse.
  const chatTurns = useChatTurns()
  // `null` quand aucun tour ne porte sur ce bloc ; sinon 'running' ou 'queued' —
  // un tour qui attend sa place n'a pas commencé, et l'annoncer « en cours »
  // serait un mensonge que l'assistant dément deux clics plus loin.
  const busyStatus = isEditMode ? blocTurnStatus(chatTurns, bloc) : null
  const isBusy = busyStatus !== null
  // Réglages avancés déclarés par la déf (`settings:`) — ce qui gouverne le
  // bloc entier et n'a rien à cliquer dans le rendu. Une entrée sans `field:`
  // ni type n'ouvrirait aucun éditeur : elle est écartée ici plutôt que de
  // poser un bouton mort dans le menu.
  // Le réglage `process` ouvre l'éditeur ECA et rebranche un formulaire sur un
  // autre traitement : c'est de l'administration, pas de l'édition de contenu.
  // La route qui le sert exige la même permission ; sans ce filtre, un rédacteur
  // verrait une entrée de menu qui n'ouvre qu'un panneau vide.
  const canAdminister = auth?.hasPermission('use advanced administration') ?? false
  const blockSettings = useMemo(
    () => (Array.isArray(blockDef?.settings) ? blockDef.settings : [])
      .filter(d => d && typeof d === 'object' && d.field && (d.type ?? d.editorType))
      .filter(d => (d.type ?? d.editorType) !== 'process' || canAdminister),
    [blockDef, canAdminister],
  )

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  // Échap = Annuler sur la confirmation de suppression.
  useEscapeClose(() => setShowDeleteConfirm(false), showDeleteConfirm)
  const [popoverField, setPopoverField] = useState(null)
  const [richTextState, setRichTextState] = useState(null)
  const [undo, setUndo] = useState(null)
  const wrapperRef = useRef(null)
  // Rail whose editor is open. The rail only shows on its host's hover/focus,
  // both of which are gone once the editor takes the focus into its portal —
  // so it is pinned open for as long as the popover it anchors lives.
  const pinnedRailRef = useRef(null)
  const unpinRail = useCallback(() => {
    pinnedRailRef.current?.classList.remove('builder-rail-pinned')
    pinnedRailRef.current = null
  }, [])

  // Commit a field edit. When the clicked field lives inside an editable unit
  // (a `[data-builder-unit]` ancestor — block/object/fragment bound to a
  // Syncable Object), write through THAT unit's store via the registry, which
  // triggers its own DataSync (per-unit native persistence). Otherwise fall
  // back to the legacy page-state path (`updateBlock` + orchestrated save).
  // The two paths are mutually exclusive — never both — so a migrating block
  // is never double-saved.
  const commitField = useCallback((fieldKey, value, unitId) => {
    // Objet vu dans un modèle : l'OBJET est la seule cible d'écriture, quelle
    // que soit l'origine du commit (texte en place, pop-in ancrée, TipTap).
    // Le refus est explicite et non un simple masquage d'affordance : rien ne
    // doit pouvoir écrire dans la page-modèle depuis cet écran — un bloc
    // partagé y modifierait l'en-tête de tout un site.
    if (objectScope) {
      objectEdit?.setValue?.(fieldKey, value)
      return
    }
    const mountedUnit = unitId ? getUnit(unitId) : null
    // Une unité que le CANEVAS persiste à sa place (aucun `submission_url` :
    // cellule de tableau de bord, bloc pas encore créé) garde quand même son
    // store comme source vivante — c'est là qu'écrivent ses champs de
    // formulaire, et c'est ce que relit un re-seed. Sans ce miroir, éditer un
    // titre en place serait aussitôt annulé par la valeur restée dans le store.
    const mirrorToStore = () => {
      if (mountedUnit && !mountedUnit.hasSubmissionUrl) mountedUnit.setFieldValue(fieldKey, value)
    }
    // Cell context (nested inside a GridLayout): fold the edit into the parent
    // block's data (the grid's `cells` field), which then persists wholesale.
    // A cell never owns a per-unit Syncable, so this override wins outright.
    if (onCommitField) {
      mirrorToStore()
      onCommitField(fieldKey, value)
      return
    }
    if (unitId) {
      const unit = mountedUnit
      if (unit?.hasSubmissionUrl) {
        // Persistable unit → write to its store; its DataSync persists it AND
        // (when its build sets `mergeKey`) mirrors the edit live to any sibling
        // unit bound to the same entity, with a single POST — see the
        // shared-syncable "merge" coordination in the DataSync element.
        unit.setFieldValue(fieldKey, value)
        return
      }
      // Unit mounted but not yet persistable (freshly added block, no
      // submission_url): fall through to the legacy page-state path so it's
      // editable immediately; the first orchestrated save creates the entity,
      // after which it round-trips with a submission_url. (A truly read-only
      // unit never reaches here — the click handler gates it out.)
    }
    mirrorToStore()
    updateBlock(bloc.id, fieldKey, value)
  }, [bloc.id, updateBlock, onCommitField, objectScope, objectEdit])

  // The live value of a top-level field: the unit store when there is one
  // (bloc.data is only the seed), otherwise the page state with the type's
  // structural defaults as fallback — what the block actually renders.
  const readFieldValue = useCallback((fieldKey, unitId) => {
    // Symétrique du commit : dans un modèle, la valeur affichée est celle de
    // l'objet en cours d'édition, pas celle du bloc qui l'affiche.
    if (objectScope) return objectEdit?.getValue?.(fieldKey)
    const unit = unitId ? getUnit(unitId) : null
    if (unit?.getFieldValue) return unit.getFieldValue(fieldKey)
    return bloc.data?.[fieldKey] ?? seedData[fieldKey]
  }, [bloc.data, seedData, objectScope, objectEdit])

  // A list cell (`rowPath` = `0.label`, `1.destinations.2.city`) is committed
  // by folding the value into its top-level array — the array is the unit of
  // persistence, exactly as when the list editor saved it whole.
  const commitAt = useCallback((fieldKey, rowPath, value, unitId) => {
    if (!rowPath) {
      commitField(fieldKey, value, unitId)
      return
    }
    commitField(fieldKey, setAtPath(readFieldValue(fieldKey, unitId), rowPath, value), unitId)
  }, [commitField, readFieldValue])

  // Plusieurs cellules commitées D'UN SEUL COUP. Deux `commitAt` de suite sur
  // le même champ liraient tous les deux la valeur d'AVANT (`bloc.data` est une
  // prop, le store d'unité un état) : le second écraserait le premier. Les
  // écritures sont donc pliées par champ, puis chaque champ est commité entier.
  const commitEdits = useCallback((edits, unitId) => {
    const byField = new Map()
    for (const { fieldKey, rowPath, value } of edits) {
      const current = byField.has(fieldKey) ? byField.get(fieldKey) : readFieldValue(fieldKey, unitId)
      byField.set(fieldKey, rowPath ? setAtPath(current, rowPath, value) : value)
    }
    for (const [fieldKey, value] of byField) commitField(fieldKey, value, unitId)
  }, [commitField, readFieldValue])

  // Les CHAMPS DU FORMULAIRE qu'une cellule déclarée `tokens:` déroule sur « @ »
  // (objet et corps d'une notification). Rien n'est déclaré : la liste est
  // dérivée du `submitData` et des intitulés du bloc, à l'ouverture de la
  // pop-in — un formulaire qui gagne un champ le propose sans rien changer ici.
  //
  // L'intitulé lu est celui qui est AFFICHÉ : le magasin de l'unité d'abord
  // (l'éditeur a pu le renommer en place), l'amorce de la déf ensuite —
  // `defaultData` est recopié dans chaque bloc à la pose, donc c'est là que
  // vivent les intitulés d'un bloc jamais retouché, pas dans `rjBuild.data`.
  const tokensForColumns = useCallback((columns, unitId) => {
    if (!Array.isArray(columns) || !columns.some(c => c && c.tokens)) return undefined
    return collectFormTokens(blockDef?.rjBuild, (key) => {
      const live = readFieldValue(key, unitId)
      return typeof live === 'string' && live !== '' ? live : blockDef?.defaultData?.[key]
    })
  }, [blockDef, readFieldValue])

  const readAt = useCallback((fieldKey, rowPath, unitId) => {
    const top = readFieldValue(fieldKey, unitId)
    return rowPath ? getAtPath(top, rowPath) : top
  }, [readFieldValue])

  // Row actions (BuilderRowToolbar / BuilderRowAction): apply to the target
  // array (relative `data-builder-row-path`, `''` = the field itself) and
  // commit the top-level field whole.
  //
  // A removal is applied on the first click — no confirmation step — and the
  // previous value of the whole field is kept so the canvas can offer "Annuler".
  // Undo is both faster than a confirm and safer than a bare delete.
  const commitRowAction = useCallback(({ action, fieldKey, arrayPath = '', rowIndex, unitId, defaultItem, toIndex = null, label = null }) => {
    const top = readFieldValue(fieldKey, unitId)
    const list = arrayPath ? getAtPath(top, arrayPath) : top
    const next = applyRowAction(list, action, rowIndex, defaultItem, toIndex)
    commitField(fieldKey, arrayPath ? setAtPath(top, arrayPath, next) : next, unitId)
    if (action === 'remove') setUndo({ fieldKey, unitId, previous: top, item: label })
  }, [readFieldValue, commitField])

  const runRowAction = useCallback((actionEl, unitId) => {
    const indexAttr = actionEl.getAttribute('data-builder-row-index')
    commitRowAction({
      action: actionEl.getAttribute('data-builder-row-action'),
      fieldKey: actionEl.getAttribute('data-builder-field'),
      arrayPath: actionEl.getAttribute('data-builder-row-path') ?? '',
      rowIndex: indexAttr != null && indexAttr !== '' ? Number(indexAttr) : null,
      unitId,
      defaultItem: parseOptions(actionEl).defaultItem,
      label: actionEl.getAttribute('data-builder-row-label'),
    })
  }, [commitRowAction])

  // ---- Reordering a list row -------------------------------------------------
  // A row is reordered by DRAGGING ITS GRIP, and — because a drag is neither
  // keyboard-operable nor available to every pointer (WCAG 2.5.7) — by the
  // grip's Arrow Up/Down or by the up/down buttons beside it. All three land
  // here. Row roots are marked by `BuilderRowToolbar` (`data-builder-row-root`
  // + field / list / position): that is how a drop target is resolved without
  // any block def having to declare one.
  const dragRow = useRef(null)
  // The zone LIT by the last dragover — `{ row, mark }`. The drop reads this
  // back instead of recomputing a target: a last pixel of travel (or a drop in
  // a gutter, where the event target is no row at all) would otherwise change
  // the destination in silence, after the editor stopped looking.
  const dropZone = useRef(null)
  // Axis per row, for the length of ONE gesture (see `rowAxis`).
  const axisCache = useRef(new Map())

  // Only the insertion line — NOT the source row's fade, which must survive
  // every dragover until the gesture ends.
  const clearDropIndicator = useCallback(() => {
    wrapperRef.current?.querySelectorAll('[data-builder-row-drop], [data-builder-row-axis]')
      .forEach(el => {
        el.removeAttribute('data-builder-row-drop')
        el.removeAttribute('data-builder-row-axis')
      })
  }, [])

  const clearDropMarks = useCallback(() => {
    dropZone.current = null
    axisCache.current.clear()
    const root = wrapperRef.current
    if (!root) return
    clearDropIndicator()
    root.querySelectorAll('.builder-row-dragging').forEach(el => el.classList.remove('builder-row-dragging'))
  }, [clearDropIndicator])

  // The grip of a given row, once the array has been re-rendered — keeps the
  // keyboard on the row it just moved instead of dropping focus to <body>.
  const focusGrip = useCallback((fieldKey, arrayPath, index) => {
    requestAnimationFrame(() => {
      const selector = '[data-builder-row-grip]'
        + '[data-builder-field="' + fieldKey + '"]'
        + '[data-builder-row-path="' + arrayPath + '"]'
        + '[data-builder-row-index="' + index + '"]'
      wrapperRef.current?.querySelector(selector)?.focus()
    })
  }, [])

  const rowSourceOf = useCallback((el) => {
    const grip = el instanceof Element ? el.closest('[data-builder-row-grip]') : null
    if (!grip) return null
    const unitEl = grip.closest('[data-builder-unit]')
    if (unitEl && unitEl.getAttribute('data-builder-unit-editable') === 'false') return null
    return {
      grip,
      // A container block (columns, grid) receives the `dragstart` of its
      // children (capture + stopPropagation), so its wrapper also spans the
      // rows of the neighbouring blocks — two blocks of the same type in two
      // columns share both field and list shape. Without this, a drop commits
      // an index of ANOTHER block's list.
      blockEl: grip.closest('.builder-block-wrapper'),
      fieldKey: grip.getAttribute('data-builder-field'),
      arrayPath: grip.getAttribute('data-builder-row-path') ?? '',
      rowIndex: Number(grip.getAttribute('data-builder-row-index')),
      unitId: unitEl?.getAttribute('data-builder-unit') ?? null,
    }
  }, [])

  const handleRowDragStart = useCallback((e) => {
    const src = rowSourceOf(e.target)
    if (!src) return
    dragRow.current = src
    axisCache.current.clear()
    const rowEl = src.grip.closest('[data-builder-row-root]')
    // Drag the ROW, not the grip. Without this the browser's ghost is a
    // snapshot of the drag source — a 22 px handle — so the gesture gave no
    // sign of what was being moved.
    if (rowEl) {
      const rect = rowEl.getBoundingClientRect()
      e.dataTransfer.setDragImage(rowEl, e.clientX - rect.left, e.clientY - rect.top)
      // The ghost is captured once dragstart returns: fade the row only after,
      // or the ghost is faded too.
      setTimeout(() => rowEl.classList.add('builder-row-dragging'), 0)
    }
    e.dataTransfer.effectAllowed = 'move'
    // Firefox starts no drag at all when the payload is empty.
    e.dataTransfer.setData('text/plain', '')
    e.stopPropagation()
  }, [rowSourceOf])

  const dropTargetOf = useCallback((target, x, y) => {
    const src = dragRow.current
    if (!src) return null
    const { row, inside } = rowUnderPointer(target, src)
    if (row) return row
    // Outside every compatible row. The fallback still holds in OPEN SPACE —
    // which includes the blank of a row that CONTAINS the target list (the
    // empty bottom of a footer column, while dragging a link into it): that
    // row is the target's container, not a rival.
    // It does NOT hold inside a row hosting no compatible row at all: that is
    // a competing list, and lighting a zone somewhere else than where the
    // editor points would accept a drop they never aimed at.
    if (inside && ![...inside.querySelectorAll('[data-builder-row-root]')].some(r => rowAccepts(r, src))) return null
    return nearestRow(src, wrapperRef.current, x, y)
  }, [])

  const handleRowDragOver = useCallback((e) => {
    if (!dragRow.current) return
    const row = dropTargetOf(e.target, e.clientX, e.clientY)
    // Over anything else, the zone goes away: leaving the list must not keep
    // promising a drop that would not happen.
    if (!row) { dropZone.current = null; clearDropIndicator(); return }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const { mark, axis } = markFor(row, e.clientX, e.clientY, axisCache.current)
    dropZone.current = { row, mark }
    if (row.dataset.builderRowDrop === mark && row.dataset.builderRowAxis === axis) return
    clearDropIndicator()
    row.setAttribute('data-builder-row-drop', mark)
    row.setAttribute('data-builder-row-axis', axis)
  }, [dropTargetOf, clearDropIndicator])

  // Move a row inside one array, or from one array to a sibling array of the
  // same field — either way ONE commit of the whole field.
  const commitRowMove = useCallback(({ fieldKey, unitId, fromPath, fromIndex, toPath, toIndex }) => {
    const top = readFieldValue(fieldKey, unitId)
    if (fromPath === toPath) {
      const list = fromPath ? getAtPath(top, fromPath) : top
      const next = applyRowAction(list, 'move', fromIndex, null, toIndex)
      commitField(fieldKey, fromPath ? setAtPath(top, fromPath, next) : next, unitId)
      return
    }
    const fromList = fromPath ? getAtPath(top, fromPath) : top
    if (!Array.isArray(fromList) || fromIndex < 0 || fromIndex >= fromList.length) return
    const moved = fromList[fromIndex]
    const nextFrom = [...fromList]
    nextFrom.splice(fromIndex, 1)
    let next = fromPath ? setAtPath(top, fromPath, nextFrom) : nextFrom
    const toList = toPath ? getAtPath(next, toPath) : next
    const nextTo = Array.isArray(toList) ? [...toList] : []
    nextTo.splice(Math.max(0, Math.min(toIndex, nextTo.length)), 0, moved)
    next = toPath ? setAtPath(next, toPath, nextTo) : nextTo
    commitField(fieldKey, next, unitId)
  }, [readFieldValue, commitField])

  const handleRowDrop = useCallback((e) => {
    const src = dragRow.current
    // The place the editor SAW lit, not a fresh hit-test of the release point.
    const aimed = dropZone.current
    const row = aimed?.row?.isConnected ? aimed.row : null
    // `rowAccepts` again: the ref is only as good as the invariant that wrote
    // it, and a commit into the wrong list is silent and destructive.
    if (!src || !row || !rowAccepts(row, src)) { clearDropMarks(); dragRow.current = null; return }
    e.preventDefault()
    e.stopPropagation()
    const toPath = row.dataset.builderRowList ?? ''
    const overIndex = Number(row.dataset.builderRowPos)
    const after = aimed.mark === 'after'
    const raw = after ? overIndex + 1 : overIndex
    // Within ONE list the destination counts in the list minus the dragged row,
    // so a drop below its own position lands one slot earlier. Across two
    // lists nothing is removed ahead of it: the raw slot is the slot.
    const sameList = toPath === src.arrayPath
    const toIndex = sameList && raw > src.rowIndex ? raw - 1 : raw
    clearDropMarks()
    dragRow.current = null
    if (sameList && toIndex === src.rowIndex) return
    commitRowMove({
      fieldKey: src.fieldKey,
      unitId: src.unitId,
      fromPath: src.arrayPath,
      fromIndex: src.rowIndex,
      toPath,
      toIndex,
    })
    if (sameList) focusGrip(src.fieldKey, src.arrayPath, toIndex)
  }, [clearDropMarks, commitRowMove, focusGrip])
  const handleRowDragEnd = useCallback(() => {
    dragRow.current = null
    clearDropMarks()
  }, [clearDropMarks])

  const applyUndo = useCallback(() => {
    if (!undo) return
    commitField(undo.fieldKey, undo.previous, undo.unitId)
    setUndo(null)
  }, [undo, commitField])

  // The undo offer expires on its own; a new removal replaces it.
  useEffect(() => {
    if (!undo) return
    const timer = setTimeout(() => setUndo(null), UNDO_WINDOW_MS)
    return () => clearTimeout(timer)
  }, [undo])

  const activateInlineText = useCallback((el, fieldKey, unitId, { multiline = false, rowPath = null } = {}) => {
    // Snapshot the children React rendered. Whatever the browser does to the
    // DOM during contentEditable (splits, removes or adds text nodes, inserts
    // <br>), the element is handed back to React in EXACTLY that shape before
    // the store commit — so React's reconciler never meets a node it doesn't
    // own (the "NotFoundError: Node.removeChild" crash) and never appends a
    // second text node next to one the browser created. This is also what
    // makes clearing a field inline safe.
    const snapshot = Array.from(el.childNodes)
    const textNode = snapshot.length === 1 && snapshot[0].nodeType === Node.TEXT_NODE ? snapshot[0] : null
    const renderedText = textNode?.data
    const savedWhiteSpace = el.style.whiteSpace
    if (multiline) el.style.whiteSpace = 'pre-wrap'
    const originalText = normalizeText(readText(el, multiline), multiline)

    // `plaintext-only` keeps Enter/paste from injecting markup; a browser that
    // doesn't know the value throws (or ignores it) → plain `true`, the
    // snapshot restore covers the markup it may inject.
    try {
      el.contentEditable = multiline ? 'plaintext-only' : 'true'
    } catch {
      el.contentEditable = 'true'
    }
    if (multiline && el.contentEditable !== 'plaintext-only') el.contentEditable = 'true'
    el.dataset.builderEditing = 'true'
    el.classList.add('builder-field-editing')

    // Place cursor at end
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)

    const restoreDom = () => {
      el.replaceChildren(...snapshot)
      if (textNode) textNode.data = renderedText
    }

    const cleanup = () => {
      el.contentEditable = 'false'
      delete el.dataset.builderEditing
      el.classList.remove('builder-field-editing')
      el.style.whiteSpace = savedWhiteSpace
      el.removeEventListener('blur', handleBlur)
      el.removeEventListener('keydown', handleKeyDown)
    }

    const handleBlur = () => {
      const newValue = normalizeText(readText(el, multiline), multiline)
      cleanup()
      restoreDom()
      if (newValue !== originalText) {
        commitAt(fieldKey, rowPath, newValue, unitId)
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        el.blur()
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        cleanup()
        restoreDom()
      }
    }

    el.addEventListener('blur', handleBlur)
    el.addEventListener('keydown', handleKeyDown)
    el.focus()
  }, [commitAt])

  // Une CATÉGORIE (`term-ref`), modifiée en place — pas dans une pop-in.
  //
  // Une pop-in pour ce type-là posait un cadre, un titre et une croix autour
  // d'un champ de saisie, et surtout DEUX ascenseurs : le sien et celui de la
  // liste, qui débordait de son corps. Rien de tout ça n'est nécessaire : une
  // catégorie est un MOT, et un mot se tape là où il s'affiche, comme un titre.
  // Ne reste, sous le mot en cours de frappe, que les propositions
  // (`InlineSuggestions`) — un seul panneau, un seul ascenseur.
  //
  // Différence avec `activateInlineText` : le texte saisi n'EST pas la valeur.
  // La valeur est `{tid, vocabulary, name}`, donc au commit on résout le nom en
  // terme — celui du site s'il existe, un terme CRÉÉ sinon (saisie libre), et
  // le champ se vide si l'on efface tout. Un échec de création ne referme pas
  // la saisie : le message s'affiche sous les propositions et le texte reste.
  const termSessionRef = useRef(null)
  const [termEdit, setTermEdit] = useState(null)

  const activateInlineTerm = useCallback((el, fieldKey, field, unitId, rowPath = null) => {
    if (el.dataset.builderEditing) return
    const vocabulary = vocabularyOf(field?.vocabularies)
    const siteId = field?.siteId ?? null

    // Même contrat que l'édition de texte : les enfants rendus par React sont
    // remis EXACTEMENT en place avant le commit, quoi que le navigateur ait
    // fait pendant la saisie.
    const snapshot = Array.from(el.childNodes)
    const textNode = snapshot.length === 1 && snapshot[0].nodeType === Node.TEXT_NODE ? snapshot[0] : null
    const renderedText = textNode?.data
    const current = readAt(fieldKey, rowPath, unitId)
    const original = (current && typeof current === 'object' ? current.name : current) ?? ''

    // `typed` reste NUL tant que l'éditeur n'a rien frappé : on vient d'ouvrir
    // sur une valeur, c'est pour en CHANGER — la liste doit donc montrer tout
    // le vocabulaire du site, avec la valeur courante surlignée, et non se
    // filtrer d'emblée sur elle (une seule proposition : celle qu'on a déjà).
    // La recherche par autocomplétion commence à la première frappe.
    const session = { terms: [], typed: null, active: null, busy: false, error: null, done: false }
    termSessionRef.current = session

    const sameName = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
    const optionsNow = () => termOptions(session.terms, session.typed ?? '')

    const sync = () => {
      if (session.done) return
      const options = optionsNow()
      // Le surlignage part sur la valeur courante — Entrée la garde alors au
      // lieu d'en choisir une autre. Résolu UNE fois, quand les termes
      // arrivent : ensuite c'est la frappe et les flèches qui commandent.
      if (session.active === null && session.terms.length > 0) {
        const index = options.findIndex((o) => o.kind === 'term' && sameName(o.term.name, original))
        session.active = index >= 0 ? index : 0
      }
      const active = Math.min(Math.max(0, session.active ?? 0), Math.max(0, options.length - 1))
      if (session.active !== null) session.active = active
      el.setAttribute('aria-activedescendant', options.length ? suggestOptionId(active) : '')
      setTermEdit({
        el,
        options,
        active,
        onPick: (option) => pick(option),
        message: session.error
          ? { kind: 'error', text: session.error }
          : session.busy
            ? { kind: 'busy', text: 'Création…' }
            : options.length === 0
              ? { kind: 'empty', text: 'Aucune catégorie sur ce site — saisissez-en une.' }
              : null,
      })
    }

    const cleanup = () => {
      session.done = true
      if (termSessionRef.current === session) termSessionRef.current = null
      el.contentEditable = 'false'
      delete el.dataset.builderEditing
      el.classList.remove('builder-field-editing')
      for (const attr of ['role', 'aria-expanded', 'aria-controls', 'aria-autocomplete', 'aria-activedescendant']) {
        el.removeAttribute(attr)
      }
      el.removeEventListener('input', handleInput)
      el.removeEventListener('keydown', handleKeyDown)
      el.removeEventListener('blur', handleBlur)
      setTermEdit(null)
    }
    const restoreDom = () => {
      el.replaceChildren(...snapshot)
      if (textNode) textNode.data = renderedText
    }
    const finish = (value) => {
      cleanup()
      restoreDom()
      commitAt(fieldKey, rowPath, value, unitId)
    }
    const cancel = () => {
      cleanup()
      restoreDom()
    }

    const createAndFinish = async (name) => {
      if (session.busy) return
      if (!vocabulary) { cancel(); return }
      session.busy = true
      session.error = null
      sync()
      try {
        const term = await createSiteTerm(vocabulary, name, siteId)
        finish(toTermValue(term, vocabulary))
      } catch (err) {
        if (session.done) return
        session.busy = false
        session.error = err.message
        sync()
        el.focus()
      }
    }

    /** Ce que vaut la saisie : un terme du site, un terme à créer, ou rien. */
    const commitTyped = () => {
      const typed = normalizeText(el.textContent ?? '', false)
      if (typed === original) { cancel(); return }
      if (typed === '') { finish(null); return }
      const known = exactTerm(session.terms, typed)
      if (known) { finish(toTermValue(known, vocabulary)); return }
      createAndFinish(typed)
    }

    const pick = (option) => {
      if (option.kind === 'term') {
        // Reprendre la catégorie déjà posée n'est pas une modification : on
        // referme sans rien écrire, plutôt que de salir la page.
        if (sameName(option.term.name, original)) { cancel(); return }
        finish(toTermValue(option.term, vocabulary))
        return
      }
      createAndFinish(option.name)
    }

    const handleInput = () => {
      session.typed = el.textContent ?? ''
      session.active = 0
      session.error = null
      sync()
    }

    const handleKeyDown = (e) => {
      const options = optionsNow()
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (options.length === 0) return
        e.preventDefault()
        const step = e.key === 'ArrowDown' ? 1 : -1
        session.active = Math.min(Math.max(0, (session.active ?? 0) + step), options.length - 1)
        sync()
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        // Tout effacer puis valider VIDE le champ : la liste continue de
        // proposer, mais c'est ce qu'on a écrit — rien — qui fait foi. Sans
        // cette réserve, Entrée reprenait la proposition surlignée et le champ
        // ne se vidait qu'en cliquant dehors.
        if (session.typed !== null && normalizeText(session.typed, false) === '') {
          commitTyped()
          return
        }
        const option = options[session.active ?? 0]
        if (option) pick(option)
        else commitTyped()
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        cancel()
      }
    }

    const handleBlur = () => {
      // Une création en cours garde la main : son échec doit pouvoir se dire.
      if (session.busy) return
      commitTyped()
    }

    try {
      el.contentEditable = 'plaintext-only'
    } catch {
      el.contentEditable = 'true'
    }
    if (el.contentEditable !== 'plaintext-only') el.contentEditable = 'true'
    el.dataset.builderEditing = 'true'
    el.classList.add('builder-field-editing')
    // Patron ARIA `combobox` : l'élément édité porte le rôle et désigne la
    // liste ; le panneau porte les options (cf. `InlineSuggestions`).
    el.setAttribute('role', 'combobox')
    el.setAttribute('aria-expanded', 'true')
    el.setAttribute('aria-controls', SUGGEST_LIST_ID)
    el.setAttribute('aria-autocomplete', 'list')

    // Le nom en place est SÉLECTIONNÉ : une catégorie se remplace plus souvent
    // qu'elle ne se corrige — taper écrit la nouvelle, une flèche garde l'ancienne.
    const range = document.createRange()
    range.selectNodeContents(el)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)

    el.addEventListener('input', handleInput)
    el.addEventListener('keydown', handleKeyDown)
    el.addEventListener('blur', handleBlur)
    el.focus()
    sync()

    // Le vocabulaire d'un site tient en quelques dizaines de termes : on le
    // charge UNE fois, on filtre en mémoire.
    loadSiteTerms(vocabulary, siteId).then((rows) => {
      if (session.done) return
      session.terms = rows
      sync()
    })
  }, [commitAt, readAt])

  // Le TEXTE INDICATIF d'un champ de formulaire, édité DANS le champ lui-même.
  //
  // Un <input> n'a pas d'enfant texte à passer en contentEditable : la surface
  // d'édition est donc sa propre VALEUR, le temps de la modification. On y
  // écrit le texte indicatif courant (setter natif + événement `input`, sinon
  // reactive-json ne voit rien), l'utilisateur le corrige avec le clavier natif
  // (curseur, sélection, collage, saisie IME), et au `blur`/Entrée la valeur
  // part dans le CHAMP, pas dans la saisie du visiteur : celle-ci est remise
  // exactement où elle était. Échap annule sans rien écrire.
  //
  // C'est la règle UX du builder appliquée à un champ de formulaire : ce qu'on
  // voit à l'écran (le texte gris) est ce qu'on modifie, en place, sans
  // affordance déportée — et le formulaire cesse d'être remplissable dans le
  // canevas, ce qu'aucun éditeur ne cherche à y faire.
  const activateInlinePlaceholder = useCallback((el, fieldKey, unitId) => {
    if (el.dataset.builderEditing) return
    const original = String(readAt(fieldKey, null, unitId) ?? el.placeholder ?? '')
    const savedValue = el.value
    const wasReadOnly = el.readOnly
    // Marquer AVANT de déverrouiller : le `MutationObserver` qui pose la
    // lecture seule tourne à chaque re-rendu du bloc — donc à chaque frappe.
    el.dataset.builderEditing = 'true'
    el.readOnly = false
    el.classList.add('builder-field-editing')
    setNativeValue(el, original)

    const cleanup = () => {
      el.removeEventListener('blur', handleBlur)
      el.removeEventListener('keydown', handleKeyDown)
      el.classList.remove('builder-field-editing')
      // Rendre le champ à l'état où le visiteur l'aurait trouvé (vide), puis
      // reverrouiller : l'ordre importe, un champ en lecture seule ignore
      // l'écriture par le setter natif dans certains navigateurs.
      setNativeValue(el, savedValue)
      el.readOnly = wasReadOnly
      delete el.dataset.builderEditing
    }

    const handleBlur = () => {
      const next = normalizeText(el.value, false)
      cleanup()
      if (next !== original) commitAt(fieldKey, null, next, unitId)
    }

    const handleKeyDown = (ev) => {
      if (ev.key === 'Enter') {
        // Entrée dans un formulaire vaut envoi : ce n'est pas ce qu'on fait ici.
        ev.preventDefault()
        el.blur()
      }
      if (ev.key === 'Escape') {
        ev.preventDefault()
        cleanup()
        el.blur()
      }
    }

    el.addEventListener('blur', handleBlur)
    el.addEventListener('keydown', handleKeyDown)
    el.focus()
    // `setSelectionRange` lève sur les types sans sélection (email, number) :
    // le curseur y reste où le navigateur l'a mis.
    try { el.setSelectionRange(el.value.length, el.value.length) } catch { /* type sans sélection */ }
  }, [readAt, commitAt])

  const activateInlineRichText = useCallback((fieldEl, fieldKey, fieldDef, unitId, rowPath = null) => {
    if (!fieldEl.parentNode) return

    // WYSIWYG : l'éditeur est monté DANS l'élément du champ, à la place du
    // contenu rendu — jamais dans un frère. Il hérite ainsi exactement de la
    // même cascade (classes du champ, sélecteurs du parent, taille de police,
    // couleur, alignement, largeur) : ce qu'on édite est ce qu'on voit.
    // Les enfants rendus par React sont mémorisés puis remis en place avant le
    // commit — même contrat que `activateInlineText` : le réconciliateur ne
    // rencontre jamais un nœud qu'il ne possède pas.
    const snapshot = Array.from(fieldEl.childNodes)
    const portalContainer = document.createElement('div')
    portalContainer.className = 'builder-richtxt-host'
    fieldEl.replaceChildren(portalContainer)
    fieldEl.classList.add('builder-field-editing')

    // Normalize the format declaration : `format: name` is a shortcut for
    // `formats: [name]`. Undefined → legacy string mode (no format handling).
    const formats = Array.isArray(fieldDef?.formats)
      ? fieldDef.formats
      : (fieldDef?.format ? [fieldDef.format] : undefined)

    setRichTextState({
      fieldKey,
      rowPath,
      unitId,
      initialValue: readAt(fieldKey, rowPath, unitId) ?? '',
      formats,
      defaultFormat: fieldDef?.defaultFormat,
      el: fieldEl,
      snapshot,
      portalContainer,
    })
  }, [readAt])

  // Open the editor of the field owning `target` (mouse or keyboard entry).
  // `point` = where to anchor a popover; `e` = the originating event.
  const activateTarget = useCallback((target, point, e) => {
    if (!isEditMode) return
    if (target.closest('.builder-richtxt-inline')) return
    if (target.closest('.builder-block-controls')) return

    // Objet dans un modèle : le seul point cliquable est une LIAISON au contenu
    // (`~~._context_.X`) qui tombe sur un champ réel du type d'objet. Une
    // liaison vers une clé qu'aucun champ ne porte — ou vers un objet
    // référencé, qui est un AUTRE contenu — reste en lecture, comme le reste du
    // modèle. L'éditeur ouvert est celui que déclare le TYPE D'OBJET, jamais le
    // bloc : c'est ce qui laisse deux types partager le même gabarit.
    if (objectScope) {
      const ctxEl = target.closest('[data-builder-context-field]')
      if (!ctxEl || ctxEl.dataset.builderEditing) return
      const bound = objectEdit?.resolve?.(ctxEl.getAttribute('data-builder-context-field'))
      if (!bound) return
      const { objectKey, field } = bound
      const editorType = field.type ?? field.editorType
      e.preventDefault()
      e.stopPropagation()
      if (editorType === 'text') {
        activateInlineText(ctxEl, objectKey, null, {})
      } else if (editorType === 'textarea') {
        activateInlineText(ctxEl, objectKey, null, { multiline: true })
      } else if (editorType === 'rich-text') {
        activateInlineRichText(ctxEl, objectKey, field, null, null)
      } else if (editorType === 'term-ref') {
        activateInlineTerm(ctxEl, objectKey, field, null, null)
      } else {
        setPopoverField({
          // Le champ ENTIER, et non `pickEditorOptions` : ce filtre existe pour
          // ce qui doit tenir dans un attribut du DOM (`data-builder-options`
          // d'un champ de bloc). Ici la déclaration vient du schéma du TYPE,
          // en mémoire — la réduire perdait les clés qu'il est seul à porter,
          // dont le `siteId` sans lequel une catégorie propose tout le
          // vocabulaire au lieu des seules taxonomies du site. C'est ce que
          // fait déjà le panneau des champs (`FieldRow`).
          ...field,
          key: objectKey,
          rowPath: null,
          unitId: null,
          anchorEl: ctxEl,
          anchorPoint: point,
          editorType,
          type: editorType,
          label: field.label ?? EDITOR_TITLES[editorType] ?? objectKey,
        })
      }
      return
    }

    // A cell can sit inside a <button> carrying the block's own action (a
    // speaker card that opens its bio). Typing a SPACE there activates that
    // button natively: the browser synthesises a click ON THE BUTTON — an
    // ancestor of the field — which would fire the action mid-edit. Such a
    // click has no pointer behind it (`detail === 0`) and wraps the element
    // being edited: the user is typing, not activating. Swallow it.
    const editing = wrapperRef.current?.querySelector('[data-builder-editing]')
    if (editing && e.detail === 0 && target.contains(editing)) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    // Row actions first: their buttons sit inside rows, possibly inside a
    // field's own click target.
    const actionEl = target.closest('[data-builder-row-action]')
    if (actionEl) {
      const unitEl = actionEl.closest('[data-builder-unit]')
      if (unitEl && unitEl.getAttribute('data-builder-unit-editable') === 'false') return
      e.preventDefault()
      e.stopPropagation()
      runRowAction(actionEl, unitEl?.getAttribute('data-builder-unit') ?? null)
      return
    }
    if (target.closest('.builder-row-toolbar') && !target.closest('[data-builder-type]')) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    // Un champ de formulaire ne se REMPLIT pas dans le canevas : il se règle.
    // Ce qu'un éditeur y voit est le TEXTE INDICATIF, c'est donc lui que le clic
    // ouvre, en place. À défaut de texte indicatif lié à une donnée, le clic est
    // renvoyé sur l'INTITULÉ du champ (même donnée dans les formulaires dont la
    // maquette ne montre que le texte indicatif). Un contrôle qui ne porte ni
    // l'un ni l'autre — facette, champ de recherche d'un aperçu — garde son
    // comportement vivant.
    const control = target.closest('input, textarea, select')
    if (control
      && !control.closest('.builder-block-controls')
      && !control.closest('.builder-row-toolbar')) {
      const controlUnit = control.closest('[data-builder-unit]')
      if (controlUnit?.getAttribute('data-builder-unit-editable') === 'false') return
      // Clic DANS le champ en cours d'édition : c'est un déplacement de curseur.
      if (control.dataset.builderEditing) return
      const placeholderField = control.getAttribute('data-builder-placeholder-field')
      if (placeholderField && control.tagName !== 'SELECT') {
        e.preventDefault()
        e.stopPropagation()
        activateInlinePlaceholder(
          control,
          placeholderField,
          controlUnit?.getAttribute('data-builder-unit') ?? null,
        )
        return
      }
      const labelEl = editableLabelOf(control, wrapperRef.current)
      // Le clic vaut alors clic sur l'intitulé : la suite l'édite en place, et
      // `preventDefault` plus bas empêche la case de basculer ou la liste de
      // s'ouvrir.
      if (labelEl) target = labelEl
    }

    const fieldEl = target.closest('[data-builder-field]')
    if (!fieldEl) return
    if (fieldEl.dataset.builderEditing) return

    const fieldKey = fieldEl.getAttribute('data-builder-field')
    // DOM-first: inline `editable:` (expanded to data-builder-type/-options)
    // wins; otherwise fall back to the block def's fields[] manifest.
    const fieldDef = resolveFieldDescriptor(fieldEl, fieldKey)
    if (!fieldDef) return

    // Resolve the owning editable unit (innermost [data-builder-unit] ancestor).
    // A read-only unit (Drupal denied write) opens no editor at all.
    const unitEl = fieldEl.closest('[data-builder-unit]')
    if (unitEl && unitEl.getAttribute('data-builder-unit-editable') === 'false') return
    const unitId = unitEl?.getAttribute('data-builder-unit') ?? null
    // A list cell edited in place (BuilderRowField) carries its row path.
    const rowPath = fieldEl.getAttribute('data-builder-row-path') || null

    e.preventDefault()
    e.stopPropagation()

    // A rail button carries the field markers but is NOT the field: there is no
    // text of its own to turn contentEditable, so whatever the type it opens in
    // the anchored popover (the registry has a surface for every type). It also
    // stays PINNED while that popover is open — the focus leaves for the portal,
    // and the rail would otherwise fade out from under its own editor.
    const railEl = fieldEl.closest('.builder-row-toolbar')
    unpinRail()
    if (railEl) {
      railEl.classList.add('builder-rail-pinned')
      pinnedRailRef.current = railEl
    }

    if (!railEl && fieldDef.editorType === 'text') {
      activateInlineText(fieldEl, fieldKey, unitId, { rowPath })
    } else if (!railEl && fieldDef.editorType === 'textarea') {
      activateInlineText(fieldEl, fieldKey, unitId, { rowPath, multiline: true })
    } else if (!railEl && fieldDef.editorType === 'rich-text') {
      activateInlineRichText(fieldEl, fieldKey, fieldDef, unitId, rowPath)
    } else if (!railEl && fieldDef.editorType === 'term-ref') {
      activateInlineTerm(fieldEl, fieldKey, fieldDef, unitId, rowPath)
    } else {
      setPopoverField({
        key: fieldKey,
        rowPath,
        unitId,
        anchorEl: fieldEl,
        anchorPoint: point,
        editorType: fieldDef.editorType,
        type: fieldDef.type ?? fieldDef.editorType,
        label: fieldDef.label,
        options: fieldDef.options,
        bundles: fieldDef.bundles,
        vocabularies: fieldDef.vocabularies,
        objectType: fieldDef.objectType,
        multiple: fieldDef.multiple,
        max: fieldDef.max,
        columns: fieldDef.columns,
        addLabel: fieldDef.addLabel,
        defaultItem: fieldDef.defaultItem,
        imageStyle: fieldDef.imageStyle,
        // Les champs du formulaire, pour les colonnes qui les insèrent (« @ »).
        fieldTokens: tokensForColumns(fieldDef.columns, unitId),
        // Dynamic-select (`es-select`, lot L3) — options fetched from an endpoint.
        optionsUrl: fieldDef.optionsUrl,
        optionsPath: fieldDef.optionsPath,
        optionsValueKey: fieldDef.optionsValueKey,
        optionsLabelKey: fieldDef.optionsLabelKey,
        // Intitulé du lien édité dans la même pop-in que son adresse : la
        // déclaration nomme le champ qui le porte (`labelColumn:` une cellule
        // voisine de la ligne, `labelField:` un champ du bloc).
        labelColumn: fieldDef.labelColumn,
        labelField: fieldDef.labelField,
        labelTitle: fieldDef.labelTitle,
      })

    }
  }, [isEditMode, objectScope, objectEdit, activateInlineText, activateInlinePlaceholder, activateInlineRichText, activateInlineTerm, runRowAction, unpinRail, tokensForColumns])

  // A link inside a block would take the editor OUT of the builder on a plain
  // click (a CTA vers /contact, un item de nav du header, une carte enveloppée
  // dans un <a>…). En mode édition tout <a href> est donc inerte : la
  // navigation est annulée en phase CAPTURE, ce qui couvre aussi les ancres
  // qu'aucun clic-champ ne voit (bloc masqué sur l'appareil courant, lien sans
  // champ éditable). « Prévisualiser » (mode édition off) les laisse vivants —
  // c'est à ça qu'il sert. Échappatoire déclarative pour un lien qui doit
  // rester cliquable en édition : `data-builder-allow-navigation` sur l'ancre
  // (ou sur un de ses parents).
  const handleWrapperClick = useCallback((e) => {
    if (isEditMode) {
      const anchor = e.target?.closest?.('a[href]')
      if (anchor
        && !anchor.closest('.builder-block-controls')
        && !anchor.closest('[data-builder-allow-navigation]')) {
        e.preventDefault()
      }
    }
    if (isHiddenOnCurrent) return
    activateTarget(e.target, { x: e.clientX, y: e.clientY }, e)
  }, [isEditMode, isHiddenOnCurrent, activateTarget])

  // Keyboard entry: Enter / Space on a focused field opens its editor exactly
  // like a click (row-action buttons are real <button>s — the browser already
  // turns their Enter/Space into a click, so they're left alone).
  const handleWrapperKeyDown = useCallback((e) => {
    // Keyboard reorder: the alternative to dragging the grip.
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const src = rowSourceOf(e.target)
      if (!src) return
      e.preventDefault()
      e.stopPropagation()
      const toIndex = src.rowIndex + (e.key === 'ArrowUp' ? -1 : 1)
      if (toIndex < 0) return
      commitRowAction({ action: 'move', ...src, toIndex })
      focusGrip(src.fieldKey, src.arrayPath, toIndex)
      return
    }
    if (e.key !== 'Enter' && e.key !== ' ') return
    const el = e.target
    if (!(el instanceof Element)) return
    // Un champ de formulaire s'atteint aussi au clavier : Entrée y ouvre
    // l'édition de son texte indicatif, comme un clic (WCAG 2.1.1 — la souris
    // n'est jamais le seul chemin vers une commande).
    const placeholderField = el.getAttribute('data-builder-placeholder-field')
    if (placeholderField && e.key === 'Enter' && !el.dataset.builderEditing && el.tagName !== 'SELECT') {
      const unitEl = el.closest('[data-builder-unit]')
      if (unitEl?.getAttribute('data-builder-unit-editable') === 'false') return
      e.preventDefault()
      e.stopPropagation()
      activateInlinePlaceholder(el, placeholderField, unitEl?.getAttribute('data-builder-unit') ?? null)
      return
    }
    if (el.closest('button, a, input, textarea, select')) return
    // Dans un modèle ouvert sur un objet, ce qui s'ouvre au clavier est ce qui
    // s'ouvre à la souris : une liaison au contenu, pas un champ du modèle.
    const marker = objectScope ? 'data-builder-context-field' : 'data-builder-field'
    if (!el.hasAttribute(marker) || el.dataset.builderEditing) return
    if (el.isContentEditable) return
    const rect = el.getBoundingClientRect()
    activateTarget(el, { x: rect.left, y: rect.bottom }, e)
  }, [activateTarget, activateInlinePlaceholder, rowSourceOf, commitRowAction, focusGrip, objectScope])

  // In edit mode every field is reachable with the keyboard: `tabindex` is
  // stamped by the runtime (not by expandEditable, which would leak into the
  // public render) and kept up to date as rows are added or removed.
  useEffect(() => {
    const root = wrapperRef.current
    if (!root) return
    const stamp = () => {
      // Modèle ouvert sur un OBJET : l'affordance ne se pose pas sur les champs
      // du modèle (ils ne sont pas modifiables ici) mais sur ses LIAISONS
      // résolues — tabulation, et le filigrane du champ vide, qui reprend
      // l'intitulé du champ d'objet. Une liaison qui ne tombe sur aucun champ
      // reste du texte : ni filet, ni tabulation, rien à cliquer.
      if (objectScope) {
        root.querySelectorAll('[data-builder-context-field]').forEach((el) => {
          const bound = isEditMode
            ? objectEdit?.resolve?.(el.getAttribute('data-builder-context-field'))
            : null
          if (bound) {
            el.classList.add('builder-context-bound')
            if (!el.hasAttribute('data-placeholder') && bound.field.label) {
              el.setAttribute('data-placeholder', bound.field.label)
            }
            if (!el.hasAttribute('tabindex') && !el.matches('button, a, input, textarea, select')) {
              el.setAttribute('tabindex', '0')
              el.dataset.builderTabstop = 'true'
            }
          } else {
            el.classList.remove('builder-context-bound')
            if (el.dataset.builderTabstop) {
              el.removeAttribute('tabindex')
              delete el.dataset.builderTabstop
            }
          }
        })
        return
      }
      root.querySelectorAll('[data-builder-field]').forEach((el) => {
        if (isEditMode) {
          if (!el.hasAttribute('tabindex') && !el.matches('button, a, input, textarea, select')) {
            el.setAttribute('tabindex', '0')
            el.dataset.builderTabstop = 'true'
          }
        } else if (el.dataset.builderTabstop) {
          el.removeAttribute('tabindex')
          delete el.dataset.builderTabstop
        }
      })
      // Et un champ de formulaire est en LECTURE SEULE tant qu'on n'édite pas
      // son texte indicatif : sans ça, un Tab, un collage ou l'autofill du
      // navigateur rempliraient le formulaire en pleine édition de la page.
      root.querySelectorAll('[data-builder-placeholder-field]').forEach((el) => {
        if (el.tagName === 'SELECT' || el.dataset.builderEditing) return
        if (isEditMode) {
          if (!el.readOnly) {
            el.readOnly = true
            el.dataset.builderReadonly = 'true'
          }
        } else if (el.dataset.builderReadonly) {
          el.readOnly = false
          delete el.dataset.builderReadonly
        }
      })
    }
    stamp()
    if (!isEditMode) return
    const observer = new MutationObserver(stamp)
    observer.observe(root, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [isEditMode, objectScope, objectEdit])

  const cleanupRichText = useCallback(() => {
    if (!richTextState) return
    const { el, snapshot } = richTextState
    if (!el) return
    el.classList.remove('builder-field-editing')
    // Rend à React exactement les enfants qu'il avait rendus (le conteneur du
    // portail disparaît avec) : le commit qui suit peut re-rendre le champ
    // sans rencontrer un nœud étranger.
    el.replaceChildren(...snapshot)
  }, [richTextState])

  const handleRichTextSave = useCallback((value) => {
    cleanupRichText()
    commitAt(richTextState.fieldKey, richTextState.rowPath, value, richTextState.unitId)
    setRichTextState(null)
  }, [richTextState, commitAt, cleanupRichText])

  const handleRichTextClose = useCallback(() => {
    cleanupRichText()
    setRichTextState(null)
  }, [richTextState, cleanupRichText])

  // L'intitulé que la pop-in d'un lien édite à côté de son adresse vit dans un
  // AUTRE champ : `labelColumn:` désigne une cellule voisine de la ligne
  // (`0.url` → `0.label`), `labelField:` un champ du bloc (un CTA seul).
  const companionOf = (field) => {
    if (!field) return null
    if (field.labelColumn) {
      if (!field.rowPath) return null
      const parent = field.rowPath.split('.').slice(0, -1).join('.')
      return { key: field.key, rowPath: parent ? `${parent}.${field.labelColumn}` : field.labelColumn }
    }
    if (field.labelField) return { key: field.labelField, rowPath: null }
    return null
  }

  const popoverCompanion = (() => {
    const target = companionOf(popoverField)
    if (!target) return null
    return {
      value: readAt(target.key, target.rowPath, popoverField.unitId) ?? '',
      title: popoverField.labelTitle ?? 'Intitulé',
    }
  })()

  // Adresse et intitulé sont commités ENSEMBLE : quand les deux vivent dans la
  // même liste (`items`), deux commits successifs se marcheraient dessus.
  const handlePopoverSave = (fieldKey, value, companionValue) => {
    unpinRail()
    const target = companionOf(popoverField)
    const edits = [{ fieldKey, rowPath: popoverField?.rowPath, value }]
    if (target && companionValue !== undefined) {
      edits.push({ fieldKey: target.key, rowPath: target.rowPath, value: companionValue })
    }
    commitEdits(edits, popoverField?.unitId)
    setPopoverField(null)
  }

  const closePopover = useCallback(() => {
    unpinRail()
    setPopoverField(null)
  }, [unpinRail])

  const handleDelete = () => {
    removeBlock(bloc.id)
    setShowDeleteConfirm(false)
  }

  // "Modify with AI": all this does is ANNOUNCE the target. The chat panel
  // (mounted by BuilderCanvas, like this wrapper) opens, pins the context and
  // focuses its composer — nothing is sent until the editor writes a request.
  const requestAiEdit = useCallback(() => {
    const detail = buildBlockContext({ bloc, def: blockDef, index, page, host })
    window.dispatchEvent(new CustomEvent(BLOCK_TARGET_EVENT, { detail }))
  }, [bloc, blockDef, index, page, host])

  // --- Redimensionner une image par point de rupture --------------------------
  // Un champ `image-drupal` reçoit, au survol ou au focus, une poignée de
  // redimensionnement et un engrenage (ImageSizingRail). Ils écrivent la clé
  // `sizing` DANS la valeur de l'image, pour la seule bande prévisualisée : la
  // barre d'appareils du builder devient le sélecteur de point de rupture.
  //
  // La cible est résolue depuis le DOM, comme le reste des affordances : le
  // champ porte déjà son type (`data-builder-type`), et l'unité qui le contient
  // dit si elle est inscriptible. Une unité en lecture seule n'affiche rien.
  const [imageTarget, setImageTarget] = useState(null)
  // Un glissé en cours tient le rail affiché : le pointeur capturé par la
  // poignée quitte le bloc, et le survol seul le ferait disparaître sous la
  // main de l'éditeur.
  const imageBusyRef = useRef(false)

  const imageTargetOf = useCallback((el) => {
    if (!(el instanceof Element)) return null
    // Les commandes du rail sont un FRÈRE du champ : sans cette sortie, les
    // survoler résoudrait « aucune image » et éteindrait le rail sous le
    // pointeur.
    if (el.closest('.builder-img-rail')) return undefined

    // Modèle ouvert sur un OBJET : l'image n'est pas un champ du bloc mais une
    // LIAISON au contenu (`~~._context_.cover`). La poignée doit y être comme
    // ailleurs — le cadrage d'une couverture se décide sur CETTE photo-là, pas
    // une fois pour tout un type. Elle écrit au même endroit que partout
    // ailleurs : la clé `sizing` DANS la valeur d'image, qui appartient ici au
    // contenu (le serveur la conserve au presave, cf. `OrbitDataEnricher`).
    //
    // C'est le TYPE D'OBJET qui dit que ce point est une image, jamais le bloc
    // — même règle que le clic (`activateTarget`). Et seule la liaison qui vise
    // l'image ELLE-MÊME est prise : `cover.alt` désigne la même valeur, mais
    // c'est une légende, pas une image à cadrer.
    if (objectScope) {
      const ctxEl = el.closest('[data-builder-context-field]')
      if (!ctxEl) return null
      const path = ctxEl.getAttribute('data-builder-context-field')
      const bound = objectEdit?.resolve?.(path)
      if (!bound || path !== bound.contextKey) return null
      if ((bound.field.type ?? bound.field.editorType) !== 'image-drupal') return null
      const ctxImg = ctxEl.matches('img') ? ctxEl : ctxEl.querySelector('img')
      if (!ctxImg?.hasAttribute('data-builder-img')) return null
      return {
        fieldEl: ctxEl,
        imgEl: ctxImg,
        fieldKey: bound.objectKey,
        rowPath: null,
        unitId: null,
        label: bound.field.label ?? bound.objectKey,
      }
    }

    const fieldEl = el.closest('[data-builder-field][data-builder-type="image-drupal"]')
    if (!fieldEl) return null
    const unitEl = fieldEl.closest('[data-builder-unit]')
    if (unitEl && unitEl.getAttribute('data-builder-unit-editable') === 'false') return null
    // `LazyImage` rend parfois un `<picture>` (une découpe par bande) : c'est
    // toujours l'`<img>` qu'on mesure et qu'on prévisualise.
    const imgEl = fieldEl.matches('img') ? fieldEl : fieldEl.querySelector('img')
    // Pas de poignée sur une image que `LazyImage` n'a pas rendue : un bloc
    // resté sur un `<img src>` brut n'a ni boîte CSS ni dérivée à changer, le
    // geste y serait une opération blanche. C'est le passage à `LazyImage` qui
    // lui ouvre la fonction, comme pour le reste du recadrage dynamique.
    if (!imgEl?.hasAttribute('data-builder-img')) return null
    return {
      fieldEl,
      imgEl,
      fieldKey: fieldEl.getAttribute('data-builder-field'),
      rowPath: fieldEl.getAttribute('data-builder-row-path') || null,
      unitId: unitEl?.getAttribute('data-builder-unit') ?? null,
      label: resolveFieldDescriptor(fieldEl, fieldEl.getAttribute('data-builder-field'))?.label,
    }
  }, [objectScope, objectEdit])

  const trackImage = useCallback((el) => {
    const next = imageTargetOf(el)
    if (next === undefined) return          // survol du rail lui-même
    setImageTarget(prev => {
      if (!next) return imageBusyRef.current ? prev : null
      if (prev?.imgEl === next.imgEl) return prev
      return next
    })
  }, [imageTargetOf])

  const imageValue = imageTarget
    ? readAt(imageTarget.fieldKey, imageTarget.rowPath, imageTarget.unitId)
    : null
  // Poignée seulement sur une valeur OBJET : sur une URL nue (forme héritée),
  // écrire un réglage transformerait la valeur en objet — le bloc qui la lit
  // comme une chaîne perdrait son image, sans erreur. Le champ reste éditable
  // par la médiathèque, il n'a simplement pas de poignée.
  const imageResizable = Boolean(imageValue) && typeof imageValue === 'object'

  const commitImageValue = useCallback((next) => {
    if (!imageTarget) return
    commitAt(imageTarget.fieldKey, imageTarget.rowPath, next, imageTarget.unitId)
  }, [imageTarget, commitAt])

  // La valeur relue AU MOMENT D'ÉCRIRE. Quand le champ appartient à une unité,
  // l'écriture va dans le store de l'unité — que ce composant n'observe pas :
  // `imageValue`, figé au dernier rendu, peut être en retard d'un réglage.
  const readImageValue = useCallback(
    () => (imageTarget ? readAt(imageTarget.fieldKey, imageTarget.rowPath, imageTarget.unitId) : null),
    [imageTarget, readAt],
  )

  // Un réglage avancé s'ouvre dans LE popover ancré des autres champs — même
  // surface, même commit : l'entrée du menu ne fait que fournir le descripteur
  // que le DOM porterait sur un champ éditable.
  //
  // L'UNITÉ propriétaire est résolue à la main : la barre du bloc vit HORS du
  // rendu (elle est un frère du contenu), donc aucun `closest()` ne la
  // rattacherait au `[data-builder-unit]` du bloc comme le fait un clic dans le
  // contenu. C'est la PREMIÈRE du sous-arbre — la plus extérieure, celle du
  // bloc lui-même ; les unités imbriquées appartiennent à ce qu'il monte.
  const openSetting = useCallback((decl, anchorEl) => {
    const type = decl.type ?? decl.editorType
    if (!type || !decl.field) return
    const unitEl = wrapperRef.current?.querySelector('[data-builder-unit]')
    // Unité en lecture seule (Drupal refuse l'écriture) : aucun éditeur, comme
    // pour un champ du rendu.
    if (unitEl && unitEl.getAttribute('data-builder-unit-editable') === 'false') return
    const rect = anchorEl?.getBoundingClientRect?.()
    setPopoverField({
      ...pickEditorOptions(decl),
      key: decl.field,
      // Le réglage `process` n'édite pas la PLACE mais la DÉFINITION : le
      // `submitData` du bloc lit son URL à la racine du rjBuild, où le magasin
      // d'une unité n'arrive jamais. Il lui faut donc de quoi écrire la déf, et
      // la valeur qu'elle porte — que `readAt` ne rendrait pas (la clé n'est
      // dans aucune unité).
      // `canEdit` vient de l'enveloppe Syncable de la déf (Drupal ne livre son
      // `submission_url` qu'à qui peut l'écrire) : sans lui le panneau offrirait
      // un bouton que le serveur refuserait.
      defNid: blockDef?.canEdit ? (blockDef.defNid ?? null) : null,
      field: decl.field,
      // Même précédence que le serveur : `defaultData` est recopié dans chaque
      // bloc à la pose, et prime donc sur `rjBuild.data` à la racine.
      seedEndpoint: [blockDef?.defaultData?.[decl.field], seedData[decl.field]]
        .find(v => typeof v === 'string' && v !== '') ?? '',
      rowPath: null,
      unitId: unitEl?.getAttribute('data-builder-unit') ?? null,
      // Les champs du formulaire, pour les colonnes qui les insèrent (« @ ») —
      // les notifications d'un formulaire se règlent aussi depuis ce menu.
      fieldTokens: tokensForColumns(decl.columns, unitEl?.getAttribute('data-builder-unit') ?? null),
      anchorEl,
      anchorPoint: rect ? { x: rect.left, y: rect.bottom } : null,
      type,
      editorType: type,
      label: decl.label ?? EDITOR_TITLES[type] ?? decl.field,
    })
  }, [blockDef, seedData, tokensForColumns])

  // L'engrenage ouvre l'éditeur `image-sizing` dans LE popover ancré des autres
  // champs — même surface, même commit : il reçoit la valeur d'image entière et
  // n'en change que le `sizing`. `device` lui dit quelle bande est à l'écran.
  const openImageSettings = useCallback(() => {
    if (!imageTarget) return
    const rect = imageTarget.imgEl.getBoundingClientRect()
    // La taille RENDUE part avec : c'est elle qui sert d'amorce quand l'image
    // n'a encore aucun réglage, pour qu'on puisse n'en taper qu'une dimension.
    const rendered = rect.width && rect.height
      ? { w: snap(rect.width), h: snap(rect.height) }
      : null
    setPopoverField({
      key: imageTarget.fieldKey,
      rowPath: imageTarget.rowPath,
      unitId: imageTarget.unitId,
      anchorEl: imageTarget.imgEl,
      anchorPoint: { x: rect.left, y: rect.bottom },
      type: 'image-sizing',
      editorType: 'image-sizing',
      label: `${imageTarget.label ?? imageTarget.fieldKey} — cadrage et tailles`,
      device: previewDevice,
      fallbackBox: rendered,
      // Le style de cadrage RENDU (celui du réglage, sinon celui du YAML du
      // bloc) : sans lui, l'éditeur afficherait « Contenir » — son défaut — sur
      // une image que le bloc sert en « Couvrir », et cacherait le réglage de
      // position, qui n'a de sens que sur un cadrage qui découpe.
      renderedOp: imageTarget.imgEl?.getAttribute('data-builder-op') || null,
      // Le rapport de l'ORIGINAL : c'est lui que « Contenir » impose, pour ne
      // pas afficher l'image entourée de vide.
      sourceRatio: sourceRatio(imageValue),
    })
  }, [imageTarget, imageValue, previewDevice])

  // Published-mode hiding is handled by real responsive CSS (the
  // `data-builder-hide` attribute on the per-bloc wrapper in BuilderCanvas +
  // the container queries in index.css), NOT by unmounting here — the old
  // `return null` keyed off `previewDevice`, which is frozen to 'desktop' when
  // published, so it never actually hid anything for real visitors. In edit
  // mode the block always renders; `isHiddenOnCurrent` only drives the muted
  // preview overlay below.

  return (
    <div
      ref={wrapperRef}
      // Nested (grid cell): fill the cell so a child can bound its own scroll
      // (in edit mode the wrapper is a real box, not display:contents, so it must
      // carry the height itself).
      // Deux modes d'édition, deux livrées. `builder-block-edit-mode` allume
      // TOUT l'outillage de page (filets des champs du bloc, rails de ligne,
      // pastilles, boutons d'ajout, neutralisation du `sticky`) : dans un
      // modèle ouvert sur un objet, rien de tout cela n'a lieu d'être — le
      // gabarit doit se lire exactement comme le visiteur le verra. D'où une
      // classe distincte, qui n'allume que les liaisons au contenu.
      className={`builder-block-wrapper relative ${isEditMode ? (objectScope ? 'builder-block-object-mode' : 'builder-block-edit-mode') : ''} ${isBusy ? 'builder-block-busy' : ''} ${nested ? 'h-full min-h-0' : ''}`}
      // In read-only render the wrapper adds no controls, so collapse it to
      // `display: contents`. This makes each block's own root a direct child
      // of the tall `<main>`, which is what allows a top-level block (e.g. a
      // site header) to use `position: sticky`. In edit mode we keep the real
      // box so the controls/overlay can position against it.
      style={isEditMode && !objectScope ? undefined : { display: 'contents' }}
      // CAPTURE phase: a block's own YAML `actions:` (a `setData` that opens a
      // speaker sheet, a dropdown toggle…) are bound on the descendants, so in
      // bubble phase they would already have fired by the time we stop
      // propagation — a click on an editable field would both edit it AND
      // trigger the block's behaviour. Capturing lets the editor win, and a
      // click that resolves to no field is left alone (facets, pagination and
      // other previews keep working in edit mode).
      // (le handler reste monté même pour un bloc masqué : il doit encore
      // neutraliser la navigation des liens en mode édition.)
      onClickCapture={handleWrapperClick}
      onKeyDownCapture={isHiddenOnCurrent || !isEditMode ? undefined : handleWrapperKeyDown}
      // Row reordering. CAPTURE on dragstart so the grip wins over a row root
      // that is natively draggable (in a list of links every <a> is).
      onDragStartCapture={isEditMode && !objectScope ? handleRowDragStart : undefined}
      onDragOver={isEditMode && !objectScope ? handleRowDragOver : undefined}
      onDrop={isEditMode && !objectScope ? handleRowDrop : undefined}
      onDragEnd={isEditMode && !objectScope ? handleRowDragEnd : undefined}
      // Survol / focus d'une image : c'est ce qui fait apparaître sa poignée de
      // redimensionnement. Rien n'est allumé en permanence — même règle que le
      // rail de ligne. Dans un modèle ouvert sur un objet, c'est `imageTargetOf`
      // qui restreint la cible aux liaisons d'image du contenu : le survol, lui,
      // est le même partout.
      onPointerOver={isEditMode && !isHiddenOnCurrent ? (e) => trackImage(e.target) : undefined}
      onFocusCapture={isEditMode && !isHiddenOnCurrent ? (e) => trackImage(e.target) : undefined}
      onPointerLeave={isEditMode ? () => { if (!imageBusyRef.current) setImageTarget(null) } : undefined}
      // Un utilisateur clavier quitte le champ au Tab, sans jamais déclencher
      // `pointerleave` : sans ceci la poignée restait montée derrière lui. Le
      // focus qui part DANS le rail (ou vers un autre champ image) ne compte
      // pas — c'est le rail qui le reçoit.
      // C'est `imageTargetOf` qui tranche, et non un sélecteur recopié : dans un
      // modèle, une image ne se reconnaît pas au même attribut, et la recopie
      // aurait démonté puis remonté la poignée en tabulant d'une image à
      // l'autre. Le rail lui-même y rend `undefined`, une autre image une
      // cible — les deux gardent la poignée.
      onBlurCapture={isEditMode ? (e) => {
        const to = e.relatedTarget
        if (imageBusyRef.current) return
        if (to && imageTargetOf(to) !== null) return
        setImageTarget(null)
      } : undefined}
    >
      {isEditMode && !objectScope && !nested && (
        <div
          id={`builder-controls-${bloc.id}`}
          className="builder-block-controls absolute top-2 right-2 z-20 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-sm px-1 py-0.5"
          onClick={e => e.stopPropagation()}
        >
          {DEVICE_ICONS.map(d => {
            const isHidden = hiddenOn.includes(d.id)
            return (
              <button
                key={d.id}
                onClick={() => toggleBlockVisibility(bloc.id, d.id)}
                className={`p-1.5 rounded transition-colors relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1 ${
                  isHidden
                    ? 'text-red-300 bg-red-50 hover:bg-red-100'
                    : 'text-gray-400 hover:text-teal-600 hover:bg-gray-50'
                }`}
                title={isHidden ? `Masqué sur ${d.label} — cliquer pour réafficher` : `Visible sur ${d.label} — cliquer pour masquer`}
                aria-label={isHidden ? `Réafficher sur ${d.label}` : `Masquer sur ${d.label}`}
              >
                <d.Icon size={14} />
                {isHidden && (
                  <span
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    aria-hidden="true"
                  >
                    <span className="block w-[22px] h-[2px] bg-red-500 rounded-full rotate-[-45deg]" />
                  </span>
                )}
              </button>
            )
          })}
          {/* Bloc partagé : dire OÙ va l'édition, MONTRER où, et offrir la
              sortie. Sans ces affordances le partage devient un piège — on
              modifie une page et on en casse quatre autres sans l'avoir su. */}
          {sharedNid && (
            <>
              <div className="w-px h-4 bg-gray-200 mx-0.5" />
              {/* Le compte VISIBLE est filtré par les droits ; `totalUsage` est
                  aveugle aux droits. Quand il est plus grand, l'éditeur tient
                  une page qu'il ne voit pas : dire « au moins », jamais un
                  chiffre minoré qui laisserait croire à un partage restreint.
                  Le nom accessible COMMENCE par le texte visible (WCAG 2.5.3),
                  puis l'explique : `title` seul n'est ni focalisable ni annoncé
                  (Opquast 159). */}
              <button
                type="button"
                ref={usageBadgeRef}
                onClick={() => setShowUsage(o => !o)}
                aria-expanded={showUsage}
                aria-controls={showUsage ? usageId : undefined}
                aria-label={`${sharedLabel.short}. ${sharedLabel.long} Afficher la liste des contenus concernés.`}
                title="Voir les contenus qui partagent ce bloc"
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-teal-800 bg-teal-50 hover:bg-teal-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
              >
                <Link2 size={12} aria-hidden="true" />
                {sharedLabel.short}
              </button>
              <button
                type="button"
                onClick={handleDetach}
                className="p-1 rounded text-gray-500 hover:text-teal-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
                aria-label="Délier ce bloc du contenu partagé"
                title="Délier : ce bloc gardera une copie du contenu et ne suivra plus les autres pages"
              >
                <Unlink size={15} aria-hidden="true" />
              </button>
            </>
          )}
          {/* La bascule retour : elle n'existe que pour un bloc délié PENDANT
              cette session d'édition (le contexte en garde la cible). Une fois
              la page rechargée, la séparation est définitive et se refait par
              « Ajouter un bloc existant ». */}
          {!sharedNid && detachedFrom?.nid && (
            <>
              <div className="w-px h-4 bg-gray-200 mx-0.5" />
              {/* Maillon INTACT (`Link`), en miroir du maillon brisé de
                  « Délier » — et non le `Link2` de la pastille d'information,
                  qui dirait « ce bloc est partagé » alors qu'il ne l'est plus.
                  Le mot est écrit : ce bouton n'apparaît qu'après un déliage,
                  trop rarement pour qu'un picto seul s'apprenne. */}
              <button
                type="button"
                onClick={handleRelink}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-teal-700 hover:bg-teal-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
                // Le nom accessible COMMENCE par le mot visible (WCAG 2.5.3),
                // puis dit à quoi le bloc se relie.
                aria-label="Relier ce bloc au contenu partagé"
                title="Relier : ce bloc suivra de nouveau le contenu commun aux autres pages — la copie faite ici sera abandonnée"
              >
                <Link size={13} aria-hidden="true" />
                Relier
              </button>
            </>
          )}
          {/* Les réglages du bloc, repliés : un picto, pas un picto par
              réglage — la barre reste lisible quelle que soit la déf. */}
          {blockSettings.length > 0 && (
            <>
              <div className="w-px h-4 bg-gray-200 mx-0.5" />
              <BlockSettingsMenu settings={blockSettings} onPick={openSetting} />
            </>
          )}
          <div className="w-px h-4 bg-gray-200 mx-0.5" />
          {/* No `defNid` means there is no definition for the assistant to edit
              (unknown type, or the registry has not loaded yet) → no button. */}
          {blockDef?.defNid && (
            <button
              type="button"
              onClick={requestAiEdit}
              className="p-1 rounded text-gray-500 hover:text-teal-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
              aria-label={`Modifier le bloc ${blockDef.name ?? bloc.type} avec l'assistant IA`}
              title="Modifier avec l'IA"
            >
              <Sparkles size={15} />
            </button>
          )}
          <div className="w-px h-4 bg-gray-200 mx-0.5" />
          <button
            onClick={() => moveBlock(bloc.id, 'up')}
            disabled={index === 0}
            className="p-1 rounded text-gray-500 hover:text-teal-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
            aria-label="Move block up"
          >
            <ChevronUp size={15} />
          </button>
          <button
            onClick={() => moveBlock(bloc.id, 'down')}
            disabled={index === total - 1}
            className="p-1 rounded text-gray-500 hover:text-teal-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
            aria-label="Move block down"
          >
            <ChevronDown size={15} />
          </button>
          <div className="w-px h-4 bg-gray-200 mx-0.5" />
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
            aria-label="Delete block"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Le voile de réécriture. Il couvre le bloc ENTIER, commandes comprises :
          déplacer ou supprimer un bloc que l'assistant réécrit ferait porter la
          réponse sur une page qui a changé sous elle. Il intercepte donc les
          clics (`cursor: progress`) au lieu de laisser passer, contrairement au
          voile « masqué sur cet appareil » qui, lui, n'annonce qu'un rendu. */}
      {/* PAS de `role=status` : plusieurs voiles coexistent maintenant — deux
          tours de front, ou une cible par TYPE qui grise toutes ses instances —
          et autant de régions vivantes feraient lire N fois la même phrase. Le
          texte reste dans le DOM (il explique pourquoi le bloc est inerte) ;
          l'annonce, elle, est faite une seule fois par le bandeau du panneau. */}
      {isBusy && (
        <div className="builder-block-busy-veil">
          <span className="builder-block-busy-badge">
            <Loader2 size={14} className="builder-block-skeleton-spin" aria-hidden="true" />
            {busyStatus === 'queued' ? 'En attente d’une place…' : 'Modification en cours…'}
          </span>
        </div>
      )}

      {isEditMode && !objectScope && isHiddenOnCurrent && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-stone-100/60 border-2 border-dashed border-stone-300 rounded pointer-events-none">
          <span className="flex items-center gap-1.5 text-xs font-medium text-stone-500 bg-white px-3 py-1.5 rounded-full shadow-sm pointer-events-auto">
            <EyeOff size={12} />
            Hidden on {previewDevice}
          </span>
        </div>
      )}

      <div
        className={`builder-block-content ${isEditMode && !objectScope ? 'builder-edit-active' : ''} ${nested ? 'h-full min-h-0' : ''} ${isEditMode && !objectScope && isHiddenOnCurrent ? 'opacity-30' : ''}`}
        style={isEditMode && !objectScope ? undefined : { display: 'contents' }}
      >
        {children}
      </div>

      {/* Où ce bloc est-il partagé ? La pastille annonce un nombre, ce panneau
          nomme les pages — et chacune s'ouvre dans un NOUVEL onglet : le
          canevas porte des modifications non enregistrées, naviguer les
          perdrait (l'ouverture est annoncée aux lecteurs d'écran). */}
      {showUsage && usagePos && createPortal(
        <div
          id={usageId}
          ref={usagePanelRef}
          role="dialog"
          aria-label="Contenus qui partagent ce bloc"
          className="popover-menu"
          style={{ ...usagePos, width: USAGE_PANEL_WIDTH, maxWidth: 'calc(100vw - 1rem)' }}
          // Le panneau est en portail dans <body>, mais son parent REACT reste
          // ce wrapper : ses clics remontent donc jusqu'à `handleWrapperClick`,
          // qui rend inerte tout `<a href>` en mode édition — les liens
          // « Ouvrir » ne s'ouvraient pas. `data-builder-allow-navigation` est
          // l'échappatoire prévue pour ça (c'est du chrome d'éditeur, pas du
          // contenu de bloc) ; le `stopPropagation` tient la machinerie
          // d'édition du canevas hors du panneau pour la même raison.
          data-builder-allow-navigation=""
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">
              {sharedUsage
                ? `Contenus partagés (${sharedUsage.pagesTotal ?? 0})`
                : 'Contenus partagés'}
            </h3>
            <button
              type="button"
              onClick={() => closeUsage()}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              aria-label="Fermer la liste des contenus partagés"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
          <div
            className="max-h-72 overflow-y-auto px-3 py-2.5"
            // La tranche change sans que le panneau bouge : sans région vivante,
            // un lecteur d'écran n'apprend jamais que la liste a changé.
            aria-live="polite"
            aria-busy={usageLoading}
          >
            {!sharedUsage && (
              <p className="text-sm text-slate-500">Recherche des contenus…</p>
            )}
            {sharedUsage && sharedPages.length === 0 && (
              <p className="text-sm text-slate-500">
                Aucun des contenus qui partagent ce bloc ne vous est accessible.
              </p>
            )}
            {sharedPages.length > 0 && (
              <ul className="flex flex-col gap-1.5">
                {sharedPages.map(usagePage => {
                  // `label` = le titre que porte le CONTENU de la page ; son
                  // `title` est la balise `<title>`, identique sur toutes les
                  // pages d'un site importé. On affiche donc le premier, et on
                  // ne retombe sur le second que s'il n'y a rien d'autre.
                  const label = usagePage.label || usagePage.title || `Page ${usagePage.nid}`
                  // La page en cours d'édition fait partie du compte : la
                  // lister sans lien (s'ouvrir soi-même n'apprend rien) est ce
                  // qui garde le total et la liste d'accord.
                  // (`Number` des deux côtés : le nid vient du JSON du proxy,
                  // celui de la page du decoupled — l'un peut être une chaîne.)
                  const isCurrent = page.pageNid != null
                    && Number(usagePage.nid) === Number(page.pageNid)
                  return (
                    <li
                      key={usagePage.nid}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5"
                    >
                      {/* Le slug sous le titre : deux pages peuvent porter le
                          même titre de contenu, c'est l'adresse qui tranche. */}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-slate-700">
                          {label}
                          {isCurrent && <span className="ml-1 text-xs text-slate-400">(cette page)</span>}
                        </span>
                        {usagePage.slug && (
                          <span className="block truncate text-xs text-slate-400">/{usagePage.slug}</span>
                        )}
                      </span>
                      {!isCurrent && usagePage.slug && (
                        <a
                          href={`/builder/${usagePage.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-secondary btn-sm shrink-0"
                        >
                          <ExternalLink size={13} aria-hidden="true" />
                          Ouvrir<span className="sr-only"> « {label} » (nouvel onglet)</span>
                        </a>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
            {hasHiddenUsage && (
              <p className="mt-1.5 text-xs text-slate-400">
                Ce bloc est aussi utilisé par des contenus auxquels vous n'avez pas accès.
              </p>
            )}
          </div>
          {/* Pagination : elle n'apparaît que s'il y a une deuxième tranche —
              sur un bloc partagé par deux pages, un pied de page vide serait du
              bruit. Le focus reste sur le bouton cliqué (la liste change au-
              dessus de lui, il ne disparaît qu'en bout de course). */}
          {(hasPrevUsage || hasNextUsage) && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2">
              <span className="text-xs text-slate-500">
                {usageFrom + 1}–{usageFrom + sharedPages.length} sur {usageTotal}
              </span>
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setUsageOffset(o => Math.max(0, o - USAGE_PAGE_SIZE))}
                  disabled={!hasPrevUsage || usageLoading}
                  className="btn btn-secondary btn-sm"
                >
                  <ChevronLeft size={13} aria-hidden="true" />
                  <span className="sr-only">Contenus précédents</span>
                </button>
                <button
                  type="button"
                  onClick={() => setUsageOffset(o => o + USAGE_PAGE_SIZE)}
                  disabled={!hasNextUsage || usageLoading}
                  className="btn btn-secondary btn-sm"
                >
                  <ChevronRight size={13} aria-hidden="true" />
                  <span className="sr-only">Contenus suivants</span>
                </button>
              </span>
            </div>
          )}
        </div>,
        document.body
      )}

      {richTextState && createPortal(
        <InlineRichTextEditor
          initialValue={richTextState.initialValue}
          formats={richTextState.formats}
          defaultFormat={richTextState.defaultFormat}
          onSave={handleRichTextSave}
          onClose={handleRichTextClose}
        />,
        richTextState.portalContainer
      )}

      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40"
          {...scrim}
        >
          <div
            className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-800 mb-2">Delete this block?</h3>
            <p className="text-sm text-gray-500 mb-5">
              This action is irreversible. The block will be removed from the page.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {undo && createPortal(
        <div className="builder-undo-toast" role="status">
          <span>{undo.item ? `« ${undo.item} » supprimé` : 'Élément supprimé'}</span>
          <button type="button" onClick={applyUndo} className="builder-undo-action">
            <Undo2 size={13} aria-hidden="true" />
            Annuler
          </button>
        </div>,
        document.body,
      )}

      {/* La poignée de l'image survolée. Elle se monte dans le bloc, à côté du
          champ : c'est ce qui la met à sa place dans l'ordre de tabulation. */}
      {isEditMode && !isHiddenOnCurrent && imageTarget && imageResizable && (
        <ImageSizingRail
          key={imageTarget.fieldKey + (imageTarget.rowPath ?? '')}
          target={imageTarget}
          device={previewDevice}
          value={imageValue}
          readValue={readImageValue}
          onCommit={commitImageValue}
          onOpenSettings={openImageSettings}
          onBusyChange={(busy) => { imageBusyRef.current = busy }}
        />
      )}

      {termEdit && (
        <InlineSuggestions
          anchorEl={termEdit.el}
          options={termEdit.options}
          activeIndex={termEdit.active}
          onPick={termEdit.onPick}
          message={termEdit.message}
        />
      )}

      {popoverField && (
        <FieldEditorPopover
          anchorEl={popoverField.anchorEl}
          anchorPoint={popoverField.anchorPoint}
          field={popoverField}
          value={readAt(popoverField.key, popoverField.rowPath, popoverField.unitId) ?? ''}
          companion={popoverCompanion}
          onSave={handlePopoverSave}

          onClose={closePopover}
        />
      )}
    </div>
  )
}
